/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DashboardSkeleton } from '../components/EnhancedSkeletonLoader';
import {
  faSearch, faBell, faUser, faUserGraduate, faCalendar, faBookmark,
  faStar, faThLarge, faFileAlt, faBriefcase, faBook,
  faPaperPlane, faEye, faMapMarkerAlt, faDollarSign, faClock,
  faPlus, faUpload, faAward, faArrowUp, faCheckCircle,
  faQuestionCircle, faCalendarCheck, faTrash, faEdit, faGraduationCap,
  faBars, faExclamationTriangle, faUserEdit, faCode, faBuilding,
  faLanguage, faProjectDiagram, faTrophy, faChartLine, faInfoCircle,
  faBullseye, faLightbulb, faSlidersH, faSave, faTimes,
  faCertificate, faExternalLinkAlt, faSpinner, faCog, faRobot, faChevronRight,
  faUndo
} from '@fortawesome/free-solid-svg-icons';
import SettingsPage from '../components/SettingsPage';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { buildApiUrl } from '../config/api';
import ThemedLoadingSpinner from '../components/ThemedLoadingSpinner';
import JobCard from '../components/JobCard';
import ApplicationTrackerTable from '../components/ApplicationTrackerTable';
import StatCard from '../components/StatCard';
import DashboardHeader from '../components/DashboardHeader';
import InternDashboardMobileHeader from '../components/InternDashboardMobileHeader';
import EmployerRecruiterHeader from '../components/EmployerRecruiterHeader';
import ResumeUpload from '../components/ResumeUpload';
import AIResumeBuilder from '../components/AIResumeBuilder';
import InterviewCalendar from '../components/InterviewCalendar';
import { useInternDashboardData } from '../hooks/useInternDashboard';
import '../styles/JobSeekerDashboard.css'; // Using same styles as JobSeeker for consistency
import '../styles/InternDashboardResponsive.css'; // Mobile responsive overrides
import '../styles/InternDashboardMobile.css'; // Professional mobile-first implementation

const openJobDetailsTab = (job) => {
  if (!job) return;
  const jobId = String(job._id || job.id || job.job_id || '').trim();
  const baseUrl = window.location.origin;

  if (jobId) {
    window.open(`${baseUrl}/job-details/${jobId}`, '_blank', 'noopener,noreferrer');
  } else {
    window.open(`${baseUrl}/job-details`, '_blank', 'noopener,noreferrer');
  }
};

// Helpers for internship company display (shared across sections)
const getDisplayCompanyName = (internship) => {
  if (!internship || typeof internship !== 'object') return 'Company';
  const hide =
    internship.showCompanyName === false ||
    internship.show_company_name === false;
  if (hide) {
    return 'Confidential';
  }
  return internship.company_name || internship.company || internship.companyName || 'Company';
};

const getCompanyInitial = (internship) => {
  const name = getDisplayCompanyName(internship);
  return name && typeof name === 'string'
    ? name.charAt(0).toUpperCase()
    : 'J';
};

const InternDashboardComplete = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient(); // React Query client for cache invalidation
  const { id: viewUserId } = useParams(); // Get userId from URL if admin is viewing another user's dashboard
  const isAdminView = !!viewUserId && (user?.role === 'admin' || user?.userType === 'admin');
  
  // Use React Query hook for data fetching (with caching and optimization)
  const { 
    dashboardData, 
    appliedInternshipIds, 
    isLoading,
    isCriticalDataLoading,
    isNonCriticalDataLoading,
    refetch 
  } = useInternDashboardData(isAdminView, viewUserId);
  
  // State
  const [activeSection, setActiveSection] = useState(() => {
    // Check URL hash first, then localStorage, then default to dashboard
    const hash = window.location.hash.replace('#', '');
    const savedSection = localStorage.getItem('internDashboardActiveSection');
    return hash || savedSection || 'dashboard';
  });
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showResumeUpload, setShowResumeUpload] = useState(false);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  
  // Keep appliedInternshipIds in state for child components that need to update it
  const [appliedIdsState, setAppliedIdsState] = useState(appliedInternshipIds);
  
  // Sync appliedInternshipIds from React Query with local state
  useEffect(() => {
    setAppliedIdsState(appliedInternshipIds);
  }, [appliedInternshipIds]);

  // Handle section change
  const handleSectionChange = (section) => {
    setShowMobileSidebar(false); // Close sidebar on mobile when section changes
    // Navigate to dedicated profile page
    if (section === 'profile') {
      navigate('/intern-profile');
      return;
    }
    
    setActiveSection(section);
    localStorage.setItem('internDashboardActiveSection', section);
    // Update URL hash without causing a page reload
    window.history.replaceState(null, null, `#${section}`);
  };

  const handleResumeUploaded = async (resumeData, aiRecommendations) => {
    console.log('📄 Resume uploaded successfully:', resumeData);
    
    try {
      // Populate profile from resume data
      console.log('🔄 Populating profile from resume data...');
      const token = localStorage.getItem('token');
      const populateResponse = await axios.post(
        buildApiUrl('/api/intern/populate-from-resume'),
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (populateResponse.data.success) {
        console.log('✅ Profile populated from resume successfully');
        setShowResumeUpload(false);
        alert('Resume uploaded and profile populated successfully! Updating dashboard...');
        await refetch();
      } else {
        throw new Error(populateResponse.data.error || 'Failed to populate profile');
      }
    } catch (error) {
      console.error('❌ Error populating profile from resume:', error);
      setShowResumeUpload(false);
      alert(`Resume uploaded successfully, but failed to populate profile: ${error.message || 'Unknown error'}. You can manually populate it later.`);
      await refetch();
    }
  };

  // Detect mobile view
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Function to refresh dashboard data (can be called from child components)
  // This maintains the same API as before for compatibility
  const refreshDashboardData = () => {
    refetch();
  };

  // Alias for compatibility with existing code that uses fetchDashboardData
  const fetchDashboardData = refetch;

  const getUserInitials = () => {
    if (!user) return 'IS';
    const firstName = user.firstName || user.first_name || '';
    const lastName = user.lastName || user.last_name || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || 'IS';
  };

  const getUserName = () => {
    if (!user) return 'Intern';
    const firstName = user.firstName || user.first_name || '';
    const lastName = user.lastName || user.last_name || '';
    return `${firstName} ${lastName}`.trim() || 'Intern';
  };

  // Show skeleton loader instead of full spinner for better perceived performance
  // Only show full spinner on initial load when no data exists
  if (isLoading && !dashboardData.stats) {
    return (
      <div style={{ padding: '20px' }}>
        <DashboardSkeleton />
      </div>
    );
  }

  const getSectionTitle = () => {
    const titleMap = {
      'dashboard': 'Dashboard',
      'internships': 'Browse Internships',
      'applications': 'My Applications',
      'saved': 'Saved Internships',
      'interviews': 'Interviews',
      'matches': 'Recommended',
      'profile': 'My Profile',
      'learning': 'Learning Resources'
    };
    return titleMap[activeSection] || 'Dashboard';
  };

  return (
    <>
      {/* Header - Mobile or Desktop */}
      {isMobile ? (
        <InternDashboardMobileHeader />
      ) : (
        <EmployerRecruiterHeader userType="intern" />
      )}

      {/* Match Jobseeker dashboard: no extra top padding under header */}
      <div style={{ paddingTop: '0' }}>
      {isAdminView && (
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          padding: '16px 24px',
          margin: '20px auto',
          maxWidth: '1200px',
          borderRadius: '8px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <FontAwesomeIcon icon={faUser} style={{ fontSize: '20px' }} />
            <div>
              <strong>Admin View Mode</strong>
              <div style={{ fontSize: '14px', opacity: 0.9, marginTop: '4px' }}>
                Viewing dashboard for user: {viewUserId}
              </div>
            </div>
          </div>
          <button
            onClick={() => navigate('/admin?view=profile-browser')}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              color: 'white',
              padding: '8px 16px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            Back to Profile Browser
          </button>
        </div>
      )}

      <div className="dashboard-container jobseeker-dashboard-container" style={{ marginTop: '0' }}>
        {/* Mobile Sidebar Overlay */}
        <div 
          className={`sidebar-overlay ${showMobileSidebar ? 'show' : ''}`}
          onClick={() => setShowMobileSidebar(false)}
        />
        
        {/* Sidebar */}
        <div className={`sidebar ${showMobileSidebar ? 'show' : ''}`} id="sidebar" style={{
          background: 'linear-gradient(180deg, #f97316 0%, #0d9488 100%)',
          backgroundColor: '#f97316',
          color: '#ffffff',
          paddingTop: '20px'
        }}>
        <div className="nav-menu">

          <div 
            className={`nav-item ${activeSection === 'dashboard' ? 'active' : ''}`}
            onClick={() => handleSectionChange('dashboard')}
          >
            <FontAwesomeIcon icon={faThLarge} />
            <span>Dashboard</span>
          </div>
          <div 
            className={`nav-item ${activeSection === 'internships' ? 'active' : ''}`}
            onClick={() => handleSectionChange('internships')}
            style={{ whiteSpace: 'nowrap' }}
          >
            <FontAwesomeIcon icon={faSearch} />
            <span style={{ whiteSpace: 'nowrap' }}>Browse Internships</span>
            <span className="badge success">NEW</span>
          </div>
          <div 
            className={`nav-item ${activeSection === 'applications' ? 'active' : ''}`}
            onClick={() => handleSectionChange('applications')}
          >
            <FontAwesomeIcon icon={faFileAlt} />
            <span>My Applications</span>
            <span className="badge">{dashboardData.stats.applications}</span>
          </div>
          <div 
            className={`nav-item ${activeSection === 'saved' ? 'active' : ''}`}
            onClick={() => handleSectionChange('saved')}
          >
            <FontAwesomeIcon icon={faBookmark} />
            <span>Saved Internships</span>
            <span className="badge">{dashboardData.stats.savedInternships}</span>
          </div>
          <div 
            className={`nav-item ${activeSection === 'interviews' ? 'active' : ''}`}
            onClick={() => handleSectionChange('interviews')}
          >
            <FontAwesomeIcon icon={faCalendarCheck} />
            <span>Interviews</span>
            <span className="badge">{dashboardData.stats.interviews}</span>
          </div>
          <div 
            className={`nav-item ${activeSection === 'matches' ? 'active' : ''}`}
            onClick={() => handleSectionChange('matches')}
          >
            <FontAwesomeIcon icon={faStar} />
            <span>Recommended</span>
          </div>
          <div 
            className={`nav-item ${activeSection === 'profile' ? 'active' : ''}`}
            onClick={() => handleSectionChange('profile')}
          >
            <FontAwesomeIcon icon={faUser} />
            <span>My Profile</span>
          </div>
          <div 
            className={`nav-item ${activeSection === 'learning' ? 'active' : ''}`}
            onClick={() => handleSectionChange('learning')}
          >
            <FontAwesomeIcon icon={faBook} />
            <span>Learning Resources</span>
          </div>
          <div 
            className={`nav-item ${activeSection === 'settings' ? 'active' : ''}`}
            onClick={() => handleSectionChange('settings')}
          >
            <FontAwesomeIcon icon={faCog} />
            <span>Settings</span>
          </div>
        </div>

        {!isAdminView && (
          <div
            className="sidebar-card"
            onClick={() => handleSectionChange('settings')}
            style={{
              margin: '0 1rem 0.5rem',
              padding: '14px 16px',
              borderRadius: '12px',
              background: 'rgba(255,255,255,0.12)',
              border: '1px solid rgba(255,255,255,0.25)',
              cursor: 'pointer'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600, color: '#fff', marginBottom: '4px' }}>
              <FontAwesomeIcon icon={faBriefcase} />
              <span>Ready for full-time jobs?</span>
            </div>
            <div style={{ fontSize: '12px', opacity: 0.9, lineHeight: 1.4, color: '#fff' }}>
              Convert to Job Seeker in Settings
            </div>
          </div>
        )}

        <div
          className="sidebar-card"
          style={{
            background: 'rgba(255,255,255,0.15)',
            border: '1px solid rgba(255,255,255,0.2)',
            marginBottom: 'max(12px, env(safe-area-inset-bottom, 0px))'
          }}
        >
          <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px', color: '#fff' }}>
            Profile Complete
          </div>
          <div style={{ fontSize: '28px', fontWeight: 700, color: '#fff', lineHeight: 1.1 }}>
            {Math.max(0, Math.min(100, dashboardData.profileCompletion || 0))}%
          </div>
        </div>
      </div>

        {/* Main Content */}
        <div className="main-content">
          {/* Content Area */}
          <div className="content-area">
          {activeSection === 'dashboard' && <DashboardSection dashboardData={dashboardData} user={user} handleSectionChange={handleSectionChange} navigate={navigate} onRefresh={fetchDashboardData} appliedInternshipIds={appliedIdsState} setAppliedInternshipIds={setAppliedIdsState} isAdminView={isAdminView} viewUserId={viewUserId} />}
          {activeSection === 'internships' && <InternshipsSection internships={dashboardData.internships} onRefresh={fetchDashboardData} appliedInternshipIds={appliedIdsState} setAppliedInternshipIds={setAppliedIdsState} profileCompletion={dashboardData.profileCompletion || 0} navigate={navigate} isAdminView={isAdminView} viewUserId={viewUserId} />}
          {activeSection === 'applications' && <ApplicationsSection applications={dashboardData.applications} onWithdrawSuccess={() => queryClient.invalidateQueries({ queryKey: ['internApplications', isAdminView, viewUserId] })} />}
          {activeSection === 'saved' && <SavedInternshipsSection savedInternships={dashboardData.savedInternships} onRefresh={fetchDashboardData} appliedInternshipIds={appliedIdsState} setAppliedInternshipIds={setAppliedIdsState} profileCompletion={dashboardData.profileCompletion || 0} navigate={navigate} isAdminView={isAdminView} viewUserId={viewUserId} />}
          {activeSection === 'interviews' && <InterviewCalendar variant="intern" />}
          {activeSection === 'matches' && <RecommendedSection internships={dashboardData.recommendations} onRefresh={fetchDashboardData} appliedInternshipIds={appliedIdsState} setAppliedInternshipIds={setAppliedIdsState} profileCompletion={dashboardData.profileCompletion || 0} navigate={navigate} isAdminView={isAdminView} viewUserId={viewUserId} />}
          {activeSection === 'profile' && <ProfileSection profile={user} />}
          {activeSection === 'learning' && <LearningResourcesSection user={user} />}
          {activeSection === 'settings' && <SettingsPage showConvertToJobSeeker={!isAdminView} />}
        </div>
        </div>
      </div>

      {/* Mobile Menu Toggle Button - Only visible on mobile via CSS */}
      <button 
        className="mobile-menu-toggle"
        onClick={() => setShowMobileSidebar(!showMobileSidebar)}
      >
        <FontAwesomeIcon icon={showMobileSidebar ? faTimes : faBars} />
      </button>
      </div>

      {/* Resume Upload Modal */}
      {showResumeUpload && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <ResumeUpload 
            onResumeUploaded={handleResumeUploaded}
            onClose={() => setShowResumeUpload(false)}
          />
        </div>
      )}
    </>
  );
};

// Dashboard Section
const DashboardSection = ({ dashboardData, user, handleSectionChange, navigate, onRefresh, appliedInternshipIds = [], setAppliedInternshipIds, isAdminView = false, viewUserId = null }) => {
  const queryClient = useQueryClient(); // React Query client for cache invalidation
  const [actionMenuData, setActionMenuData] = useState(null);
  const [actionMenuPosition, setActionMenuPosition] = useState({ left: 0, top: 0 });

  useEffect(() => {
    const closeMenu = (event) => {
      if (
        !event.target.closest('.actions-menu-portal') &&
        !event.target.closest('.actions-toggle')
      ) {
        setActionMenuData(null);
      }
    };
    const handleScroll = () => setActionMenuData(null);

    document.addEventListener('mousedown', closeMenu);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);

    return () => {
      document.removeEventListener('mousedown', closeMenu);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, []);

  const handleApply = async (internship) => {
    // Check if profile is at least 75% complete
    const profileCompletion = dashboardData.profileCompletion || 0;
    if (profileCompletion < 75) {
      const confirmMessage = `⚠️ Profile Completion Required\n\nYour profile is currently ${profileCompletion}% complete.\n\nYou must complete at least 75% of your profile to apply for internships.\n\nWould you like to go to your profile page now to complete it?`;
      if (window.confirm(confirmMessage)) {
        navigate('/intern-profile');
      }
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const internshipId = String(internship._id || internship.id);
      
      if (!internshipId || internshipId === 'undefined') {
        alert('Error: Invalid internship ID');
        return;
      }
      
      const response = await axios.post(
        buildApiUrl(`/api/intern/apply/${internshipId}`),
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.status === 200) {
        alert('Application submitted successfully! 🎉');
        if (setAppliedInternshipIds) {
          setAppliedInternshipIds(prev => [...prev, internshipId]);
        }
        // Refetch applications so My Applications updates immediately
        queryClient.invalidateQueries({ queryKey: ['internApplications', isAdminView, viewUserId] });
        setActionMenuData(null);
      }
    } catch (error) {
      console.error('Error applying:', error);
      alert(error.response?.data?.error || 'Failed to submit application');
    }
  };

  const handleSave = async (internship) => {
    try {
      const token = localStorage.getItem('token');
      const internshipId = String(internship._id || internship.id || '').trim();
      
      if (!internshipId || internshipId === 'undefined') {
        alert('Error: Unable to identify this internship.');
        return;
      }

      const response = await axios.post(
        buildApiUrl(`/api/intern/save/${internshipId}`),
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.status === 200) {
        const action = response.data?.action === 'removed' ? 'removed from' : 'saved to';
        alert(`Internship ${action} your saved list.`);
        // Invalidate saved internships cache (no full refresh)
        queryClient.invalidateQueries({ 
          queryKey: ['savedInternships', isAdminView, viewUserId] 
        });
        // Also update stats cache since saved count changed
        queryClient.invalidateQueries({ 
          queryKey: ['internProfile', isAdminView, viewUserId] 
        });
        setActionMenuData(null);
      }
    } catch (error) {
      console.error('Error saving internship:', error);
      alert('Failed to save internship. Please try again.');
    }
  };

  const handleViewDetails = (internship) => {
    openJobDetailsTab(internship);
    setActionMenuData(null);
  };

  const handleMenuOpen = (event, internship) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setActionMenuPosition({
      left: rect.left + window.scrollX,
      top: rect.bottom + window.scrollY + 4
    });
    setActionMenuData(internship);
  };

  const parseInterviewDateTime = (application) => {
    const rawDate = application.interviewDate || application.interview_date;
    if (!rawDate) return null;
    const [datePart] = rawDate.split('T');
    if (!datePart) return null;
    const [year, month, day] = datePart.split('-').map(Number);
    if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) return null;
    const startTime = application.interview_start_time || application.interview_time || '';
    const endTime = application.interview_end_time || '';
    const [hours = 0, minutes = 0] = startTime ? startTime.split(':').map(Number) : [0, 0];
    const date = new Date(year, month - 1, day, hours || 0, minutes || 0);
    return {
      date,
      dateKey: datePart,
      startTime,
      endTime,
      mode: application.interview_mode || 'online'
    };
  };

  const formatTimeWindow = (start, end) => {
    if (!start && !end) return 'Time TBD';
    const format = (value) => {
      if (!value) return '';
      const [hours, minutes] = value.split(':').map(Number);
      if (Number.isNaN(hours)) return value;
      const date = new Date(0, 0, 0, hours, minutes || 0);
      return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    };
    const startStr = format(start);
    const endStr = format(end);
    return endStr ? `${startStr} - ${endStr}` : startStr || endStr || 'Time TBD';
  };

  const upcomingInterviews = dashboardData.applications
    .map((application) => {
      const parsed = parseInterviewDateTime(application);
      if (!parsed) return null;
      return {
        ...parsed,
        application
      };
    })
    .filter(Boolean)
    .filter(({ date }) => date >= new Date())
    .sort((a, b) => a.date - b.date);

  const nextInterview = upcomingInterviews[0];
  const nextInterviewLabel = nextInterview
    ? `Next: ${nextInterview.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`
    : (dashboardData.stats.interviews === 0 ? 'No interviews yet' : 'Check schedule');
  return (
  <div>
    {/* Profile Completion - Fully Consistent with JobSeeker */}
    {/* Profile completion moved to sidebar */}

    {/* Stats Grid - Modern Design with Real Data */}
    <div className="stats-grid">
      <StatCard
        title="Applications Sent"
        value={dashboardData.stats.applications}
        trend={(() => {
          // Calculate applications this week
          const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
          const thisWeekApps = dashboardData.applications.filter(app => {
            const appDate = new Date(app.appliedDate || app.applied_date || app.createdAt || app.created_at);
            return appDate >= oneWeekAgo;
          }).length;
          return thisWeekApps > 0 ? Math.round((thisWeekApps / Math.max(dashboardData.stats.applications, 1)) * 100) : 0;
        })()}
        trendValue={(() => {
          const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
          return dashboardData.applications.filter(app => {
            const appDate = new Date(app.appliedDate || app.applied_date || app.createdAt || app.created_at);
            return appDate >= oneWeekAgo;
          }).length.toString();
        })()}
        trendLabel="this week"
        icon={faPaperPlane}
        iconColor="#f97316"
      />
      
      <StatCard
        title="Interviews Scheduled"
        value={dashboardData.stats.interviews}
        trend={0}
        trendValue=""
        trendLabel={nextInterviewLabel}
        icon={faCalendarCheck}
        iconColor="#10b981"
      />
      
      <StatCard
        title="Profile Views"
        value={dashboardData.stats.profileViews}
        trend={(() => {
          // Calculate percentage of views this month vs total
          const viewsThisMonth = dashboardData.profileViewsThisMonth || 0;
          const total = dashboardData.stats.profileViews || 1;
          return viewsThisMonth > 0 ? Math.round((viewsThisMonth / total) * 100) : 0;
        })()}
        trendValue={dashboardData.profileViewsThisMonth?.toString() || '0'}
        trendLabel="this month"
        icon={faEye}
        iconColor="#0d9488"
      />
      
      <StatCard
        title="Saved Opportunities"
        value={dashboardData.stats.savedInternships}
        trend={(() => {
          // Calculate new recommendations today
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const newToday = dashboardData.recommendations.filter(rec => {
            const postedDate = new Date(rec.postedDate || rec.posted_date || rec.created_at);
            return postedDate >= today;
          }).length;
          return newToday > 0 ? 100 : 0;
        })()}
        trendValue={(() => {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          return dashboardData.recommendations.filter(rec => {
            const postedDate = new Date(rec.postedDate || rec.posted_date || rec.created_at);
            return postedDate >= today;
          }).length.toString();
        })()}
        trendLabel="new matches today"
        icon={faBookmark}
        iconColor="#f59e0b"
      />
    </div>

    {/* Upcoming interview banner removed */}
    {/* Alerts removed per request */}

    {/* Main Grid */}
    <div style={{ marginTop: '20px' }}>
      <div className="card">
        <div className="card-header" style={{ justifyContent: 'center', flexDirection: 'column', gap: '15px', alignItems: 'center' }}>
          <h3 className="card-title" style={{ textAlign: 'center', width: '100%', justifyContent: 'center', margin: '0 auto' }}>Recommended for You</h3>
          <button className="btn btn-secondary btn-sm" onClick={() => handleSectionChange('matches')} style={{ alignSelf: 'center', margin: '0 auto' }}>
            View All
          </button>
        </div>
        {dashboardData.recommendations && dashboardData.recommendations.length > 0 ? (
          <div className="applications-table-wrapper" style={{ overflow: 'visible', position: 'relative' }}>
            <table className="applications-table" style={{ overflow: 'visible' }}>
              <thead>
                <tr>
                  <th style={{ width: '26%' }}>Role / Company</th>
                  <th style={{ width: '18%' }}>Location</th>
                  <th style={{ width: '18%' }}>Type</th>
                  <th style={{ width: '18%' }}>Posted On</th>
                  <th style={{ width: '20%' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {dashboardData.recommendations.slice(0, 3).map((internship) => (
                  <tr className="application-row" key={internship._id || internship.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div className="candidate-avatar" style={{
                          width: '42px',
                          height: '42px',
                          borderRadius: '12px',
                          background: 'linear-gradient(135deg, #FF8A65, #14b8a6)',
                          color: '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: '700',
                          fontSize: '16px'
                        }}>
                          {getCompanyInitial(internship)}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <strong style={{ color: '#0f172a' }}>
                            {internship.job_title || internship.title || 'Internship'}
                          </strong>
                          <span style={{ color: '#0ea5e9', fontWeight: 600, fontSize: '14px' }}>
                            {getDisplayCompanyName(internship)}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div style={{ color: '#475569' }}>
                        {internship.location || internship.city || 'Not specified'}
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', color: '#475569' }}>
                        <span>{internship.job_type || internship.remote_option || 'Full-time'}</span>
                        <small style={{ color: '#94a3b8' }}>{internship.experience_level || 'Entry Level'}</small>
                      </div>
                    </td>
                    <td>
                      <div style={{ color: '#475569' }}>
                        {(() => {
                          const posted = internship.posted_date || internship.postedDate || internship.createdAt;
                          if (!posted) return '—';
                          const date = new Date(posted);
                          return `${date.toLocaleDateString('en-GB')} | ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
                        })()}
                      </div>
                    </td>
                    <td style={{ position: 'relative', overflow: 'visible' }}>
                      <button
                        className="actions-toggle"
                        onClick={(e) => handleMenuOpen(e, internship)}
                      >
                        Actions ▾
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state" style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px' }}>
            <FontAwesomeIcon icon={faBriefcase} style={{ fontSize: '48px', color: '#ccc', marginBottom: '15px' }} />
            <p style={{ textAlign: 'center', margin: '0' }}>No internships available at the moment</p>
            <p style={{ fontSize: '14px', color: '#999', marginTop: '8px', textAlign: 'center', margin: '8px 0 0 0' }}>New opportunities will appear here</p>
          </div>
        )}
      </div>
    </div>
    {actionMenuData && createPortal(
      <div
        className="actions-menu actions-menu-portal"
        style={{
          position: 'absolute',
          left: `${actionMenuPosition.left}px`,
          top: `${actionMenuPosition.top}px`
        }}
      >
        <button
          onClick={async () => {
            await handleApply(actionMenuData);
            setActionMenuData(null);
          }}
          disabled={appliedInternshipIds.includes(String(actionMenuData._id || actionMenuData.id))}
        >
          {appliedInternshipIds.includes(String(actionMenuData._id || actionMenuData.id)) ? 'Applied' : 'Apply'}
        </button>
        <button
          onClick={() => {
            handleViewDetails(actionMenuData);
            setActionMenuData(null);
          }}
        >
          View Details
        </button>
        <button
          onClick={async () => {
            await handleSave(actionMenuData);
            setActionMenuData(null);
          }}
        >
          Save Internship
        </button>
      </div>,
      document.body
    )}
  </div>
);
};

// Internships Section
const InternshipsSection = ({ internships, onRefresh, appliedInternshipIds = [], setAppliedInternshipIds, profileCompletion = 0, navigate, isAdminView = false, viewUserId = null }) => {
  const queryClient = useQueryClient(); // React Query client for cache invalidation
  const [filteredInternships, setFilteredInternships] = useState(internships);
  const [actionMenuData, setActionMenuData] = useState(null);
  const [actionMenuPosition, setActionMenuPosition] = useState({ left: 0, top: 0 });
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    sort: 'recent'
  });

  useEffect(() => {
    const closeMenu = (event) => {
      if (
        !event.target.closest('.actions-menu-portal') &&
        !event.target.closest('.actions-toggle')
      ) {
        setActionMenuData(null);
      }
    };
    const handleScroll = () => setActionMenuData(null);

    document.addEventListener('mousedown', closeMenu);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);

    return () => {
      document.removeEventListener('mousedown', closeMenu);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, []);

  useEffect(() => {
    let filtered = [...internships];

    // Apply search query filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(job => {
        const title = (job.job_title || job.jobTitle || job.title || '').toLowerCase();
        const company = (job.company_name || job.company || '').toLowerCase();
        const location = (job.location || job.city || '').toLowerCase();
        const jobType = (job.job_type || job.remote_option || '').toLowerCase();
        const description = (job.description || job.job_description || '').toLowerCase();
        
        return title.includes(query) || 
               company.includes(query) || 
               location.includes(query) || 
               jobType.includes(query) ||
               description.includes(query);
      });
    }

    // Apply sorting
    if (filters.sort === 'stipend-high') {
      filtered.sort((a, b) => {
        const aMax = a.salary_range?.max || 0;
        const bMax = b.salary_range?.max || 0;
        return bMax - aMax;
      });
    } else if (filters.sort === 'recent') {
      filtered.sort((a, b) => {
        const aDate = new Date(a.posted_date || 0);
        const bDate = new Date(b.posted_date || 0);
        return bDate - aDate;
      });
    }

    setFilteredInternships(filtered);
  }, [searchQuery, filters, internships]);

  const handleApply = async (job) => {
    // Check if profile is at least 75% complete
    if (profileCompletion < 75) {
      const confirmMessage = `⚠️ Profile Completion Required\n\nYour profile is currently ${profileCompletion}% complete.\n\nYou must complete at least 75% of your profile to apply for internships.\n\nWould you like to go to your profile page now to complete it?`;
      if (window.confirm(confirmMessage)) {
        navigate('/intern-profile');
      }
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const internshipId = String(job._id || job.id);
      
      if (!internshipId || internshipId === 'undefined') {
        alert('Error: Invalid internship ID');
        return;
      }

      const response = await axios.post(
        buildApiUrl(`/api/intern/apply/${internshipId}`),
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.status === 200) {
        alert('Application submitted successfully! 🎉');
        if (setAppliedInternshipIds) {
          setAppliedInternshipIds(prev => [...prev, internshipId]);
        }
        queryClient.invalidateQueries({ queryKey: ['internApplications', isAdminView, viewUserId] });
        return {
          coins_awarded: response.data?.coins_awarded || false,
          coins_amount: response.data?.coins_amount || 0
        };
      }
    } catch (error) {
      console.error('Error applying to internship:', error);
      if (error.response?.data?.error) {
        alert(`Error: ${error.response.data.error}`);
      } else {
        alert('Failed to submit application. Please try again.');
      }
    }
  };

  const handleSave = async (jobId) => {
    try {
      const token = localStorage.getItem('token');
      const internshipId = String(jobId?._id || jobId?.id || jobId || '').trim();

      if (!internshipId || internshipId === 'undefined') {
        alert('Error: Unable to identify this internship.');
        return;
      }

      const response = await axios.post(
        buildApiUrl(`/api/intern/save/${internshipId}`),
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.status === 200) {
        const action = response.data?.action === 'removed' ? 'removed from' : 'saved to';
        alert(`Internship ${action} your saved list.`);
        // Invalidate saved internships cache (no full refresh)
        queryClient.invalidateQueries({ 
          queryKey: ['savedInternships', isAdminView, viewUserId] 
        });
        // Also update stats cache since saved count changed
        queryClient.invalidateQueries({ 
          queryKey: ['internProfile', isAdminView, viewUserId] 
        });
      }
    } catch (error) {
      console.error('Error saving internship:', error);
      alert('Failed to save internship. Please try again.');
    }
  };

  const handleViewDetails = (job) => {
    openJobDetailsTab(job);
  };

  const handleMenuOpen = (event, internship) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setActionMenuPosition({
      left: rect.left + window.scrollX,
      top: rect.bottom + window.scrollY + 4
    });
    setActionMenuData(internship);
  };

  return (
    <div>

      <div className="filters" style={{ marginBottom: '20px', display: 'flex', gap: '15px', alignItems: 'center' }}>
        <div style={{ width: '25%', position: 'relative' }}>
          <input
            type="text"
            placeholder="Search internships by title, company, location..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 15px',
              border: '2px solid #e2e8f0',
              borderRadius: '10px',
              fontSize: '14px',
              outline: 'none',
              transition: 'border-color 0.3s',
              backgroundColor: '#fff'
            }}
            onFocus={(e) => e.target.style.borderColor = '#f97316'}
            onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
          />
        </div>
        <select 
          className="filter-select"
          value={filters.sort}
          onChange={(e) => setFilters({...filters, sort: e.target.value})}
          style={{ minWidth: '200px', marginLeft: 'auto' }}
        >
          <option value="recent">Sort By: Most Recent</option>
          <option value="relevance">Relevance</option>
          <option value="stipend-high">Stipend: High to Low</option>
        </select>
      </div>

      {filteredInternships && filteredInternships.length > 0 ? (
        <div className="applications-table-wrapper" style={{ overflow: 'visible', position: 'relative' }}>
          <table className="applications-table" style={{ overflow: 'visible' }}>
            <thead>
              <tr>
                <th style={{ width: '26%' }}>Role / Company</th>
                <th style={{ width: '18%' }}>Location</th>
                <th style={{ width: '18%' }}>Type</th>
                <th style={{ width: '18%' }}>Posted On</th>
                <th style={{ width: '20%' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredInternships.map((internship) => (
                <tr className="application-row" key={internship._id || internship.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div className="candidate-avatar" style={{
                        width: '42px',
                        height: '42px',
                        borderRadius: '12px',
                        background: 'linear-gradient(135deg, #FF8A65, #14b8a6)',
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: '700',
                        fontSize: '16px'
                      }}>
                        {getCompanyInitial(internship)}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <strong style={{ color: '#0f172a' }}>
                          {internship.job_title || internship.title || 'Internship'}
                        </strong>
                        <span style={{ color: '#0ea5e9', fontWeight: 600, fontSize: '14px' }}>
                          {getDisplayCompanyName(internship)}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div style={{ color: '#475569' }}>
                      {internship.location || internship.job_location || internship.jobLocation || internship.city || 'Not specified'}
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', color: '#475569' }}>
                      <span>{internship.job_type || internship.remote_option || 'Full-time'}</span>
                      <small style={{ color: '#94a3b8' }}>{internship.experience_level || 'Entry Level'}</small>
                    </div>
                  </td>
                  <td>
                    <div style={{ color: '#475569' }}>
                      {(() => {
                        const posted = internship.posted_date || internship.postedDate || internship.createdAt;
                        if (!posted) return '—';
                        const date = new Date(posted);
                        return `${date.toLocaleDateString('en-GB')} | ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
                      })()}
                    </div>
                  </td>
                  <td style={{ position: 'relative', overflow: 'visible' }}>
                    <button
                      className="actions-toggle"
                      onClick={(e) => handleMenuOpen(e, internship)}
                    >
                      Actions ▾
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="card">
          <div className="empty-state">
            <FontAwesomeIcon icon={faSearch} style={{ fontSize: '64px', color: '#ccc', marginBottom: '20px' }} />
            <h3>No Internships Found</h3>
            <p style={{ marginBottom: '10px' }}>Try adjusting your filters to see more results.</p>
            <p style={{ color: '#999', fontSize: '14px' }}>Check back later for new opportunities!</p>
          </div>
        </div>
      )}
      {actionMenuData && createPortal(
        <div
          className="actions-menu actions-menu-portal"
          style={{
            position: 'absolute',
            left: `${actionMenuPosition.left}px`,
            top: `${actionMenuPosition.top}px`
          }}
        >
          <button
            onClick={async () => {
              await handleApply(actionMenuData);
              setActionMenuData(null);
            }}
            disabled={appliedInternshipIds.includes(String(actionMenuData._id || actionMenuData.id))}
          >
            {appliedInternshipIds.includes(String(actionMenuData._id || actionMenuData.id)) ? 'Applied' : 'Apply'}
          </button>
          <button
            onClick={() => {
              handleViewDetails(actionMenuData);
              setActionMenuData(null);
            }}
          >
            View Details
          </button>
          <button
            onClick={async () => {
              await handleSave(actionMenuData._id || actionMenuData.id);
              setActionMenuData(null);
            }}
          >
            Save Internship
          </button>
        </div>,
        document.body
      )}
    </div>
  );
};

// Applications Section
const ApplicationsSection = ({ applications, onWithdrawSuccess }) => {
  const [filteredApplications, setFilteredApplications] = useState(applications || []);
  const [statusPopup, setStatusPopup] = useState(null);
  const [withdrawingId, setWithdrawingId] = useState(null);
  const previousStatusesRef = React.useRef({});
  const shownMessagesRef = React.useRef(new Set());
  const [filters, setFilters] = useState({
    status: 'all',
    location: 'all',
    mode: 'all',
    sort: 'recent'
  });

  const getStatusMessage = (status, jobTitle, companyName) => {
    const statusLower = (status || '').toLowerCase();
    const title = jobTitle || 'this internship';
    const company = companyName || 'the company';
    
    switch (statusLower) {
      case 'hired':
      case 'accepted':
        return {
          message: `🎉 Congratulations! You've been accepted for ${title} at ${company}! Welcome aboard!`,
          type: 'success',
          icon: '🎉'
        };
      case 'offered':
        return {
          message: `🎊 Great news! You've received an internship offer for ${title} at ${company}! Check your email for details.`,
          type: 'success',
          icon: '🎊'
        };
      case 'interview_scheduled':
      case 'interview scheduled':
      case 'interview':
        return {
          message: `🚀 Exciting! Your interview has been scheduled for ${title} at ${company}. Prepare well and showcase your potential!`,
          type: 'info',
          icon: '🚀'
        };
      case 'shortlisted':
        return {
          message: `⭐ Excellent! You've been shortlisted for ${title} at ${company}. You're one step closer to this amazing opportunity!`,
          type: 'success',
          icon: '⭐'
        };
      case 'reviewed':
      case 'reviewing':
        return {
          message: `👀 Your application for ${title} at ${company} is being reviewed. The employer will get back to you soon!`,
          type: 'info',
          icon: '👀'
        };
      case 'rejected':
        return {
          message: `💪 Thank you for applying to ${title} at ${company}. While this wasn't the right fit, keep applying - your perfect internship is coming!`,
          type: 'neutral',
          icon: '💪'
        };
      default:
        return null; // No message for pending or unknown statuses
    }
  };

  useEffect(() => {
    let filtered = [...(applications || [])];

    // Check for status changes and show popup if needed
    filtered.forEach(app => {
      const appId = app._id || app.id;
      const currentStatus = (app.status || 'pending').toLowerCase();
      const previousStatus = previousStatusesRef.current[appId] || 'pending';
      
      // Only show popup if status changed and is not pending
      if (currentStatus !== previousStatus && currentStatus !== 'pending') {
        const messageKey = `${appId}-${currentStatus}`;
        
        // Check if we've already shown this message (from localStorage)
        const storedShown = localStorage.getItem(`statusMessage-${messageKey}`);
        
        if (!storedShown && !shownMessagesRef.current.has(messageKey)) {
          const statusInfo = getStatusMessage(
            currentStatus,
            app.job_title || app.title || app.position,
            app.company_name || app.company
          );
          
          if (statusInfo) {
            // Mark as shown
            localStorage.setItem(`statusMessage-${messageKey}`, 'true');
            shownMessagesRef.current.add(messageKey);
            
            // Show popup
            setStatusPopup({
              status: currentStatus,
              jobTitle: app.job_title || app.title || app.position || 'this internship',
              companyName: app.company_name || app.company || 'the company',
              message: statusInfo.message,
              type: statusInfo.type,
              icon: statusInfo.icon
            });
          }
        }
      }
      
      // Update previous status
      previousStatusesRef.current[appId] = currentStatus;
    });

    // Apply filters
    if (filters.status !== 'all') {
      filtered = filtered.filter(app => {
        const status = (app.status || '').toLowerCase();
        return status === filters.status.toLowerCase();
      });
    }

    if (filters.location !== 'all') {
      if (filters.location === 'remote') {
        filtered = filtered.filter(app => {
          const location = (app.location || app.job_location || '').toLowerCase();
          return location.includes('remote');
        });
      } else {
        filtered = filtered.filter(app => {
          const location = (app.location || app.job_location || '').toLowerCase();
          return location.includes(filters.location.toLowerCase());
        });
      }
    }

    if (filters.mode !== 'all') {
      filtered = filtered.filter(app => {
        const mode = (app.interview_mode || app.mode || app.job_type || '').toLowerCase();
        return mode.includes(filters.mode.toLowerCase());
      });
    }

    // Apply sorting
    if (filters.sort === 'recent') {
      filtered.sort((a, b) => {
        const aDate = new Date(a.appliedDate || a.applied_date || a.createdAt || 0);
        const bDate = new Date(b.appliedDate || b.applied_date || b.createdAt || 0);
        return bDate - aDate;
      });
    } else if (filters.sort === 'stipend-high') {
      filtered.sort((a, b) => {
        const aStipend = a.stipend_range?.max || a.stipend?.max || 0;
        const bStipend = b.stipend_range?.max || b.stipend?.max || 0;
        return bStipend - aStipend;
      });
    }

    setFilteredApplications(filtered);
  }, [filters, applications]);

  const getStatusColor = (status) => {
    const statusLower = (status || '').toLowerCase();
    switch (statusLower) {
      case 'pending':
        return '#f59e0b';
      case 'reviewed':
      case 'reviewing':
        return '#3b82f6';
      case 'shortlisted':
        return '#10b981';
      case 'rejected':
        return '#ef4444';
      case 'accepted':
      case 'hired':
        return '#059669';
      default:
        return '#6b7280';
    }
  };

  const formatStipend = (app) => {
    // Check multiple possible field names for stipend/salary
    const stipend = app.stipend_range || app.stipend || app.salary_range || app.salary || 
                    app.internshipDetails?.stipend || app.internshipDetails?.salary_range;
    
    if (!stipend || stipend === 'Not specified') return 'Not specified';
    
    if (typeof stipend === 'object') {
      const min = stipend.min || stipend.minimum || 0;
      const max = stipend.max || stipend.maximum || 0;
      if (min && max && min > 0 && max > 0) {
        return `KSh ${min.toLocaleString()} - ${max.toLocaleString()}`;
      } else if (min && min > 0) {
        return `KSh ${min.toLocaleString()}+`;
      } else if (max && max > 0) {
        return `KSh ${max.toLocaleString()}`;
      }
    }
    
    if (typeof stipend === 'string' && stipend.trim() && stipend !== 'Not specified') {
      return stipend;
    }
    
    if (typeof stipend === 'number' && stipend > 0) {
      return `KSh ${stipend.toLocaleString()}`;
    }
    
    return 'Not specified';
  };

  return (
    <div>
      {/* Status Update Popup Card */}
      {statusPopup && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          zIndex: 10000,
          maxWidth: '450px',
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
          border: `2px solid ${statusPopup.type === 'success' ? '#10b981' : statusPopup.type === 'info' ? '#3b82f6' : '#6b7280'}`,
          padding: '20px',
          animation: 'slideInRight 0.3s ease-out'
        }}>
          <style>{`
            @keyframes slideInRight {
              from {
                transform: translateX(100%);
                opacity: 0;
              }
              to {
                transform: translateX(0);
                opacity: 1;
              }
            }
          `}</style>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: '15px'
          }}>
            <div style={{ flex: 1 }}>
              <div style={{
                fontSize: '18px',
                fontWeight: '700',
                color: statusPopup.type === 'success' ? '#10b981' : statusPopup.type === 'info' ? '#3b82f6' : '#6b7280',
                marginBottom: '8px'
              }}>
                STATUS
              </div>
              <div style={{
                fontSize: '14px',
                color: '#475569',
                lineHeight: '1.6'
              }}>
                {statusPopup.message}
              </div>
            </div>
            <button
              onClick={() => setStatusPopup(null)}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '20px',
                color: '#94a3b8',
                cursor: 'pointer',
                padding: '0',
                width: '24px',
                height: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#f1f5f9';
                e.target.style.color = '#64748b';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = '#94a3b8';
              }}
            >
              ×
            </button>
          </div>
        </div>
      )}
      <div className="filters" style={{ marginBottom: '20px' }}>
        <select 
          className="filter-select"
          value={filters.status}
          onChange={(e) => setFilters({...filters, status: e.target.value})}
        >
          <option value="all">All Status</option>
          <option value="pending">Pending</option>
          <option value="reviewed">Reviewed</option>
          <option value="shortlisted">Shortlisted</option>
          <option value="accepted">Accepted</option>
          <option value="rejected">Rejected</option>
        </select>
        <select 
          className="filter-select"
          value={filters.location}
          onChange={(e) => setFilters({...filters, location: e.target.value})}
        >
          <option value="all">All Locations</option>
          <option value="nairobi">Nairobi, Kenya</option>
          <option value="mombasa">Mombasa, Kenya</option>
          <option value="remote">Remote</option>
        </select>
        <select 
          className="filter-select"
          value={filters.mode}
          onChange={(e) => setFilters({...filters, mode: e.target.value})}
        >
          <option value="all">All Modes</option>
          <option value="internship">Internship</option>
          <option value="full-time">Full-time</option>
          <option value="part-time">Part-time</option>
          <option value="remote">Remote</option>
        </select>
        <select 
          className="filter-select"
          value={filters.sort}
          onChange={(e) => setFilters({...filters, sort: e.target.value})}
        >
          <option value="recent">Sort By: Most Recent</option>
          <option value="stipend-high">Stipend: High to Low</option>
        </select>
      </div>

      {filteredApplications && filteredApplications.length > 0 ? (
        <div className="applications-table-wrapper" style={{ overflowX: 'auto', overflowY: 'visible', position: 'relative', width: '100%' }}>
          <table className="applications-table" style={{ width: '100%', tableLayout: 'auto', minWidth: '100%' }}>
            <thead>
              <tr>
                <th style={{ width: '20%' }}>Company</th>
                <th style={{ width: '17%' }}>Position</th>
                <th style={{ width: '14%' }}>Location</th>
                <th style={{ width: '14%' }}>Stipend</th>
                <th style={{ width: '11%' }}>Applied</th>
                <th style={{ width: '9%' }}>Mode</th>
                <th style={{ width: '15%', minWidth: '120px' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredApplications.map((application) => (
                <tr className="application-row" key={application._id || application.id}>
                  <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <strong style={{ color: '#0f172a' }}>
                          {application.company_name || application.company || application.companyName || 'N/A'}
                        </strong>
                    </div>
                  </td>
                  <td>
                    <div style={{ color: '#475569', fontWeight: 500 }}>
                      {application.jobTitle || application.job_title || application.title || application.position || 'Position'}
                    </div>
                  </td>
                  <td>
                    <div style={{ color: '#475569' }}>
                      {application.location || application.job_location || application.jobLocation || application.city || 'Not specified'}
                    </div>
                  </td>
                  <td>
                    <div style={{ color: '#475569' }}>
                      {formatStipend(application)}
                    </div>
                  </td>
                  <td>
                    <div style={{ color: '#475569' }}>
                      {(() => {
                        const applied = application.appliedDate || application.applied_date || application.appliedAt || application.createdAt;
                        if (!applied) return '—';
                        const date = new Date(applied);
                        return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
                      })()}
                    </div>
                  </td>
                  <td>
                    <div style={{ color: '#475569' }}>
                      {(() => {
                        // Check interview_mode first (for interview mode), then check job type/mode
                        const interviewMode = application.interview_mode || application.interviewMode;
                        const jobType = application.job_type || application.jobType || application.type;
                        const remoteOption = application.remote_option || application.remoteOption;
                        const workMode = application.workMode || application.work_mode;
                        
                        // If interview is scheduled, show interview mode (online/offline/hybrid)
                        if (interviewMode) {
                          return interviewMode.charAt(0).toUpperCase() + interviewMode.slice(1).toLowerCase();
                        }
                        
                        // Otherwise show work mode (remote/hybrid/onsite) or job type
                        if (workMode) {
                          return workMode.charAt(0).toUpperCase() + workMode.slice(1).toLowerCase();
                        }
                        if (remoteOption) {
                          return remoteOption.charAt(0).toUpperCase() + remoteOption.slice(1).toLowerCase();
                        }
                        if (jobType) {
                          return jobType.charAt(0).toUpperCase() + jobType.slice(1).toLowerCase();
                        }
                        
                        return 'Not specified';
                      })()}
                    </div>
                  </td>
                  <td style={{ overflow: 'visible', whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'flex-start' }}>
                      <div style={{ 
                        display: 'inline-flex', 
                        alignItems: 'center', 
                        gap: '6px',
                        padding: '4px 12px',
                        borderRadius: '20px',
                        backgroundColor: `${getStatusColor(application.status)}20`,
                        border: `1px solid ${getStatusColor(application.status)}`,
                        color: getStatusColor(application.status),
                        fontSize: '12px',
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        overflow: 'visible'
                      }}>
                        <span style={{
                          width: '6px',
                          height: '6px',
                          borderRadius: '50%',
                          backgroundColor: getStatusColor(application.status),
                          flexShrink: 0
                        }}></span>
                        <span style={{ whiteSpace: 'nowrap' }}>
                        {(application.status || 'Pending').charAt(0).toUpperCase() + (application.status || 'Pending').slice(1).toLowerCase()}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={async () => {
                          const appId = application._id || application.id;
                          if (!appId) return;
                          if (!window.confirm('Withdraw this application? You can apply again from Browse Internships.')) return;
                          setWithdrawingId(appId);
                          try {
                            const token = localStorage.getItem('token');
                            await axios.delete(buildApiUrl(`/api/intern/applications/${appId}/withdraw`), {
                              headers: { Authorization: `Bearer ${token}` }
                            });
                            if (onWithdrawSuccess) onWithdrawSuccess();
                          } catch (err) {
                            alert(err.response?.data?.error || 'Failed to withdraw application');
                          } finally {
                            setWithdrawingId(null);
                          }
                        }}
                        disabled={withdrawingId === (application._id || application.id)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#0ea5e9',
                          fontSize: '11px',
                          fontWeight: 600,
                          cursor: withdrawingId === (application._id || application.id) ? 'wait' : 'pointer',
                          padding: '2px 0',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                        title="Withdraw application and apply again later"
                      >
                        {withdrawingId === (application._id || application.id) ? (
                          <FontAwesomeIcon icon={faSpinner} spin />
                        ) : (
                          <FontAwesomeIcon icon={faUndo} />
                        )}
                        Retract
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="card">
          <div className="empty-state">
            <FontAwesomeIcon icon={faFileAlt} style={{ fontSize: '64px', color: '#ccc', marginBottom: '20px' }} />
            <h3>No Applications Found</h3>
            <p style={{ marginBottom: '10px' }}>Try adjusting your filters to see more results.</p>
            <p style={{ color: '#999', fontSize: '14px' }}>You haven't applied to any internships yet.</p>
          </div>
        </div>
      )}
    </div>
  );
};

// Saved Internships Section
const SavedInternshipsSection = ({ savedInternships, onRefresh, appliedInternshipIds = [], setAppliedInternshipIds, profileCompletion = 0, navigate, isAdminView = false, viewUserId = null }) => {
  const queryClient = useQueryClient(); // React Query client for cache invalidation
  const handleApply = async (job) => {
    // Check if profile is at least 75% complete
    if (profileCompletion < 75) {
      const confirmMessage = `⚠️ Profile Completion Required\n\nYour profile is currently ${profileCompletion}% complete.\n\nYou must complete at least 75% of your profile to apply for internships.\n\nWould you like to go to your profile page now to complete it?`;
      if (window.confirm(confirmMessage)) {
        navigate('/intern-profile');
      }
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const internshipId = String(job._id || job.id);
      
      if (!internshipId || internshipId === 'undefined') {
        alert('Error: Invalid internship ID');
        return;
      }

      const response = await axios.post(
        buildApiUrl(`/api/intern/apply/${internshipId}`),
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.status === 200) {
        alert('Application submitted successfully! 🎉');
        if (setAppliedInternshipIds) {
          setAppliedInternshipIds(prev => [...prev, internshipId]);
        }
        queryClient.invalidateQueries({ queryKey: ['internApplications', isAdminView, viewUserId] });
      }
    } catch (error) {
      console.error('Error applying to internship:', error);
      if (error.response?.data?.error) {
        alert(`Error: ${error.response.data.error}`);
      } else {
        alert('Failed to submit application. Please try again.');
      }
    }
  };

  const handleUnsave = async (jobId) => {
    try {
      const token = localStorage.getItem('token');
      const internshipId = String(jobId?._id || jobId?.id || jobId || '').trim();
      if (!internshipId || internshipId === 'undefined') {
        alert('Error: Unable to identify this internship.');
        return;
      }

      // The save endpoint is a toggle - it will unsave if already saved
      const response = await axios.post(
        buildApiUrl(`/api/intern/save/${internshipId}`),
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.status === 200) {
        const action = response.data?.action || 'removed';
        if (action === 'unsaved' || action === 'removed') {
          // Successfully removed - invalidate only saved internships cache (no full refresh)
          queryClient.invalidateQueries({ 
            queryKey: ['savedInternships', isAdminView, viewUserId] 
          });
          // Also update stats cache since saved count changed
          queryClient.invalidateQueries({ 
            queryKey: ['internProfile', isAdminView, viewUserId] 
          });
        } else {
          // This shouldn't happen, but handle it
          console.warn('Unexpected action:', action);
          queryClient.invalidateQueries({ 
            queryKey: ['savedInternships', isAdminView, viewUserId] 
          });
        }
      }
    } catch (error) {
      console.error('Error unsaving internship:', error);
      if (error.response?.data?.error) {
        alert(`Error: ${error.response.data.error}`);
      } else {
        alert('Failed to remove internship. Please try again.');
      }
    }
  };

  const handleViewDetails = (job) => {
    openJobDetailsTab(job);
  };

  return (
    <div>
      {savedInternships.length > 0 ? (
        <div className="jobs-grid" style={{ display: 'grid', gap: '20px', gridTemplateColumns: '1fr' }}>
          {savedInternships.map((internship, index) => (
            <JobCard 
              key={index} 
              job={internship} 
              onApply={handleApply}
              onSave={handleUnsave}
              onViewDetails={handleViewDetails}
              isSaved={true}
              isAlreadyApplied={appliedInternshipIds.includes(String(internship._id || internship.id))}
            />
          ))}
        </div>
      ) : (
        <div className="card">
          <div className="empty-state">
            <FontAwesomeIcon icon={faBookmark} style={{ fontSize: '64px', color: '#ccc' }} />
            <h3>No Saved Internships</h3>
            <p>Bookmark internships you're interested in to view them later</p>
          </div>
        </div>
      )}
    </div>
  );
};

// Interviews Section
const InterviewsSection = ({ interviews }) => {
  const normalizeInterview = (interview) => {
    if (!interview) return null;
    const dateRaw = interview.interview_date || interview.interviewDate;
    if (!dateRaw) return null;
    const [datePart] = dateRaw.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) return null;

    const startTime = interview.interview_start_time || interview.interview_time || '';
    const endTime = interview.interview_end_time || '';
    const [startHour = 0, startMinute = 0] = startTime ? startTime.split(':').map(Number) : [0, 0];
    const startDate = new Date(year, month - 1, day, startHour || 0, startMinute || 0);

    return {
      ...interview,
      interview_date: datePart,
      interview_start_time: startTime,
      interview_end_time: endTime,
      interview_date_obj: startDate,
      interview_mode: interview.interview_mode || 'online',
      interview_title: interview.job_title || interview.jobTitle || 'Interview',
      company_name: interview.company_name || interview.companyName || 'Company'
    };
  };

  const formatTimeRange = (start, end) => {
    if (!start && !end) return 'Time TBD';
    const format = (value) => {
      if (!value) return '';
      const [hours, minutes] = value.split(':').map(Number);
      if (Number.isNaN(hours)) return value;
      return new Date(0, 0, 0, hours, minutes || 0).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    };
    const startStr = format(start);
    const endStr = format(end);
    return endStr ? `${startStr} - ${endStr}` : startStr || endStr || 'Time TBD';
  };

  const normalizedInterviews = interviews
    .map(normalizeInterview)
    .filter(Boolean)
    .sort((a, b) => a.interview_date_obj - b.interview_date_obj);

  return (
  <div>
    <div className="alert info">
      <FontAwesomeIcon icon={faInfoCircle} style={{ fontSize: '24px' }} />
      <div>
        <strong>Tip:</strong> Research the company and prepare examples of your projects before the interview.
      </div>
    </div>

      {normalizedInterviews.length > 0 ? (
        <div className="interviews-grid">
          {normalizedInterviews.map((interview, index) => (
            <div key={index} className="card interview-detail-card">
              <div className="interview-detail-header">
                <div>
                  <span className="interview-detail-date">
                    {interview.interview_date_obj.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                  </span>
                  <h3>{interview.interview_title}</h3>
                  <p className="interview-detail-company">{interview.company_name}</p>
                </div>
                <span className={`interview-mode-badge ${interview.interview_mode}`}>
                  {interview.interview_mode}
                </span>
              </div>
              <div className="interview-detail-body">
                <div>
                  <label>Time</label>
                  <p>{formatTimeRange(interview.interview_start_time, interview.interview_end_time)}</p>
                </div>
                {interview.interview_notes && (
                  <div>
                    <label>Notes</label>
                    <p>{interview.interview_notes}</p>
                  </div>
                )}
                {interview.interview_link && (
                  <div>
                    <label>Meeting Link</label>
                    <a
                      href={interview.interview_link}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {interview.interview_link}
                      <FontAwesomeIcon icon={faExternalLinkAlt} style={{ marginLeft: '6px', fontSize: '12px' }} />
                    </a>
                  </div>
                )}
                {interview.interview_location && (
                  <div>
                    <label>Location</label>
                    <p>{interview.interview_location}</p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
    ) : (
      <div className="card">
        <div className="empty-state">
          <FontAwesomeIcon icon={faCalendarCheck} style={{ fontSize: '64px', color: '#ccc' }} />
          <h3>No Interviews Scheduled</h3>
          <p>Your upcoming interviews will appear here</p>
        </div>
      </div>
    )}
  </div>
);
};

// Recommended Section - AI-powered recommendations based on preferred role
const RecommendedSection = ({ internships, onRefresh, appliedInternshipIds = [], setAppliedInternshipIds, profileCompletion = 0, navigate, isAdminView = false, viewUserId = null }) => {
  const queryClient = useQueryClient(); // React Query client for cache invalidation
  const [actionMenuData, setActionMenuData] = useState(null);
  const [actionMenuPosition, setActionMenuPosition] = useState({ left: 0, top: 0 });

  useEffect(() => {
    const closeMenu = (event) => {
      if (
        !event.target.closest('.actions-menu-portal') &&
        !event.target.closest('.actions-toggle')
      ) {
        setActionMenuData(null);
      }
    };
    const handleScroll = () => setActionMenuData(null);

    document.addEventListener('mousedown', closeMenu);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);

    return () => {
      document.removeEventListener('mousedown', closeMenu);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, []);

  const handleApply = async (job) => {
    // Check if profile is at least 75% complete
    if (profileCompletion < 75) {
      const confirmMessage = `⚠️ Profile Completion Required\n\nYour profile is currently ${profileCompletion}% complete.\n\nYou must complete at least 75% of your profile to apply for internships.\n\nWould you like to go to your profile page now to complete it?`;
      if (window.confirm(confirmMessage)) {
        navigate('/intern-profile');
      }
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const internshipId = String(job._id || job.id);
      
      if (!internshipId || internshipId === 'undefined') {
        alert('Error: Invalid internship ID');
        return;
      }

      const response = await axios.post(
        buildApiUrl(`/api/intern/apply/${internshipId}`),
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.status === 200) {
        alert('Application submitted successfully! 🎉');
        if (setAppliedInternshipIds) {
          setAppliedInternshipIds(prev => [...prev, internshipId]);
        }
        queryClient.invalidateQueries({ queryKey: ['internApplications', isAdminView, viewUserId] });
        setActionMenuData(null);
      }
    } catch (error) {
      console.error('Error applying to internship:', error);
      if (error.response?.data?.error) {
        alert(`Error: ${error.response.data.error}`);
      } else {
        alert('Failed to submit application. Please try again.');
      }
    }
  };

  const handleSave = async (jobId) => {
    try {
      const token = localStorage.getItem('token');
      const internshipId = String(jobId?._id || jobId?.id || jobId || '').trim();
      if (!internshipId || internshipId === 'undefined') {
        alert('Error: Unable to identify this internship.');
        return;
      }

      const response = await axios.post(
        buildApiUrl(`/api/intern/save/${internshipId}`),
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.status === 200) {
        const action = response.data?.action === 'removed' ? 'removed from' : 'saved to';
        alert(`Internship ${action} your list.`);
        // Invalidate saved internships cache (no full refresh)
        queryClient.invalidateQueries({ 
          queryKey: ['savedInternships', isAdminView, viewUserId] 
        });
        // Also update stats cache since saved count changed
        queryClient.invalidateQueries({ 
          queryKey: ['internProfile', isAdminView, viewUserId] 
        });
        setActionMenuData(null);
      }
    } catch (error) {
      console.error('Error saving internship:', error);
      alert('Failed to save internship. Please try again.');
    }
  };

  const handleViewDetails = (job) => {
    openJobDetailsTab(job);
    setActionMenuData(null);
  };

  const handleMenuOpen = (event, internship) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setActionMenuPosition({
      left: rect.left + window.scrollX,
      top: rect.bottom + window.scrollY + 4
    });
    setActionMenuData(internship);
  };

  return (
    <div>
      {internships && internships.length > 0 && (
        <div className="alert success">
          <FontAwesomeIcon icon={faStar} style={{ fontSize: '24px' }} />
          <div>
            Based on your <strong>preferred role</strong>, skills, and interests, we found <strong>{internships.length} personalized matches</strong> for you!
          </div>
        </div>
      )}
      {internships && internships.length > 0 ? (
        <div className="applications-table-wrapper" style={{ overflow: 'visible', position: 'relative' }}>
          <table className="applications-table" style={{ overflow: 'visible' }}>
            <thead>
              <tr>
                <th style={{ width: '26%' }}>Role / Company</th>
                <th style={{ width: '18%' }}>Location</th>
                <th style={{ width: '18%' }}>Type</th>
                <th style={{ width: '18%' }}>Posted On</th>
                <th style={{ width: '20%' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {internships.map((internship) => (
                <tr className="application-row" key={internship._id || internship.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div className="candidate-avatar" style={{
                        width: '42px',
                        height: '42px',
                        borderRadius: '12px',
                        background: 'linear-gradient(135deg, #FF8A65, #14b8a6)',
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: '700',
                        fontSize: '16px'
                      }}>
                        {getCompanyInitial(internship)}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <strong style={{ color: '#0f172a' }}>
                          {internship.job_title || internship.title || 'Internship'}
                        </strong>
                        <span style={{ color: '#0ea5e9', fontWeight: 600, fontSize: '14px' }}>
                          {getDisplayCompanyName(internship)}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div style={{ color: '#475569' }}>
                      {internship.location || internship.job_location || internship.jobLocation || internship.city || 'Not specified'}
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', color: '#475569' }}>
                      <span>{internship.job_type || internship.remote_option || 'Full-time'}</span>
                      <small style={{ color: '#94a3b8' }}>{internship.experience_level || 'Entry Level'}</small>
                    </div>
                  </td>
                  <td>
                    <div style={{ color: '#475569' }}>
                      {(() => {
                        const posted = internship.posted_date || internship.postedDate || internship.createdAt;
                        if (!posted) return '—';
                        const date = new Date(posted);
                        return `${date.toLocaleDateString('en-GB')} | ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
                      })()}
                    </div>
                  </td>
                  <td style={{ position: 'relative', overflow: 'visible' }}>
                    <button
                      className="actions-toggle"
                      onClick={(e) => handleMenuOpen(e, internship)}
                    >
                      Actions ▾
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="card">
          <div className="empty-state" style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <FontAwesomeIcon icon={faStar} style={{ fontSize: '64px', color: '#ccc', marginBottom: '20px' }} />
            <h3 style={{ textAlign: 'center', margin: '0 0 1rem 0' }}>No AI Recommendations Yet</h3>
            <p style={{ marginBottom: '10px', textAlign: 'center', maxWidth: '600px' }}>Our AI is analyzing available internships to find your perfect match!</p>
            <p style={{ color: '#999', fontSize: '14px', textAlign: 'center', maxWidth: '600px' }}>💡 Tip: Add your <strong>Preferred Internship Title</strong> in My Profile for better AI recommendations</p>
          </div>
        </div>
      )}
      {actionMenuData && createPortal(
        <div
          className="actions-menu actions-menu-portal"
          style={{
            position: 'absolute',
            left: `${actionMenuPosition.left}px`,
            top: `${actionMenuPosition.top}px`
          }}
        >
          <button
            onClick={async () => {
              await handleApply(actionMenuData);
              setActionMenuData(null);
            }}
            disabled={appliedInternshipIds.includes(String(actionMenuData._id || actionMenuData.id))}
          >
            {appliedInternshipIds.includes(String(actionMenuData._id || actionMenuData.id)) ? 'Applied' : 'Apply'}
          </button>
          <button
            onClick={() => {
              handleViewDetails(actionMenuData);
              setActionMenuData(null);
            }}
          >
            View Details
          </button>
          <button
            onClick={async () => {
              await handleSave(actionMenuData._id || actionMenuData.id);
              setActionMenuData(null);
            }}
          >
            Save Internship
          </button>
        </div>,
        document.body
      )}
    </div>
  );
};


// Profile Section
const ProfileSection = ({ profile }) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [profileData, setProfileData] = useState({
    fullName: `${profile?.firstName || ''} ${profile?.lastName || ''}`.trim(),
    email: profile?.email || '',
    phone: profile?.phone || '',
    location: profile?.location || '',
    dob: '',
    gender: '',
    nationality: '',
    residentCountry: '',
    city: '',
    address: '',
    workAuth: '',
    prefLoc1: '',
    prefLoc2: '',
    prefLoc3: '',
    relocate: '',
    mode: '',
    academicLevel: '',
    objective: '',
    industry: '',
    role: '',
    duration: '',
    availability: '',
    timing: '',
    stipend: '',
    unpaid: '',
    credit: '',
    hobbies: '',
    whyInternship: ''
  });

  const toggleEditMode = () => {
    setIsEditMode(!isEditMode);
  };

  const saveProfile = () => {
    // Here you would typically save to backend
    console.log('Profile saved:', profileData);
    setIsEditMode(false);
    alert('Profile updated successfully!');
  };

  const cancelEdit = () => {
    setIsEditMode(false);
  };

  const handleInputChange = (field, value) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <div>
      <h1 style={{ marginBottom: '25px' }}>My Profile</h1>
      
      {/* Profile Header */}
      <div className="profile-header">
        <h1 style={{ fontSize: '28px', marginBottom: '5px' }}>{profileData.fullName}</h1>
        <p style={{ fontSize: '16px', opacity: 0.9 }}>Computer Science Student • University of Nairobi</p>
        <div style={{ marginTop: '15px' }}>
          <button className="btn" style={{ background: 'white', color: '#22c55e' }} onClick={toggleEditMode}>
            <FontAwesomeIcon icon={faEdit} /> <span id="editBtnText">{isEditMode ? 'Cancel Edit' : 'Edit Profile'}</span>
          </button>
        </div>
      </div>

      {/* Profile Content */}
      <div className={`profile-content ${isEditMode ? 'edit-mode' : ''}`}>

        {/* Personal Information */}
        <div className="profile-section">
          <h3 className="profile-section-title">
            <FontAwesomeIcon icon={faUser} />
            Personal Information
          </h3>
          <div className="profile-grid two-column">
            <div className="profile-field">
              <div className="profile-field-label">Full Name</div>
              <div className="profile-field-value" id="fullName">{profileData.fullName}</div>
              <input 
                type="text" 
                className="profile-field-input" 
                id="fullNameInput" 
                value={profileData.fullName}
                onChange={(e) => handleInputChange('fullName', e.target.value)}
              />
            </div>
            <div className="profile-field">
              <div className="profile-field-label">Date of Birth</div>
              <div className="profile-field-value" id="dob">{new Date(profileData.dob).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
              <input 
                type="date" 
                className="profile-field-input" 
                id="dobInput" 
                value={profileData.dob}
                onChange={(e) => handleInputChange('dob', e.target.value)}
              />
            </div>
            <div className="profile-field">
              <div className="profile-field-label">Gender</div>
              <div className="profile-field-value" id="gender">{profileData.gender.charAt(0).toUpperCase() + profileData.gender.slice(1)}</div>
              <select 
                className="profile-field-input" 
                id="genderInput"
                value={profileData.gender}
                onChange={(e) => handleInputChange('gender', e.target.value)}
              >
                <option value="female">Female</option>
                <option value="male">Male</option>
                <option value="other">Other</option>
                <option value="prefer-not-to-say">Prefer not to say</option>
              </select>
            </div>
            <div className="profile-field">
              <div className="profile-field-label">Email Address</div>
              <div className="profile-field-value" id="email">{profileData.email}</div>
              <input 
                type="email" 
                className="profile-field-input" 
                id="emailInput" 
                value={profileData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
              />
            </div>
            <div className="profile-field">
              <div className="profile-field-label">Primary Phone</div>
              <div className="profile-field-value" id="phone">{profileData.phone || 'Not provided'}</div>
              <input 
                type="tel" 
                className="profile-field-input" 
                id="phoneInput" 
                value={profileData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
              />
            </div>
            <div className="profile-field">
              <div className="profile-field-label">Alternative Phone</div>
              <div className="profile-field-value" id="altPhone">+254 722 987 654</div>
              <input 
                type="tel" 
                className="profile-field-input" 
                id="altPhoneInput" 
                value="+254 722 987 654"
                onChange={(e) => handleInputChange('altPhone', e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Nationality & Residency */}
        <div className="profile-section">
          <h3 className="profile-section-title">
            <FontAwesomeIcon icon={faLanguage} />
            Nationality & Residency
          </h3>
          <div className="profile-grid two-column">
            <div className="profile-field">
              <div className="profile-field-label">Nationality</div>
              <div className="profile-field-value" id="nationality">{profileData.nationality}</div>
              <input 
                type="text" 
                className="profile-field-input" 
                id="nationalityInput" 
                value={profileData.nationality}
                onChange={(e) => handleInputChange('nationality', e.target.value)}
              />
            </div>
            <div className="profile-field">
              <div className="profile-field-label">Resident Country</div>
              <div className="profile-field-value" id="residentCountry">{profileData.residentCountry}</div>
              <input 
                type="text" 
                className="profile-field-input" 
                id="residentCountryInput" 
                value={profileData.residentCountry}
                onChange={(e) => handleInputChange('residentCountry', e.target.value)}
              />
            </div>
            <div className="profile-field">
              <div className="profile-field-label">Current City</div>
              <div className="profile-field-value" id="city">{profileData.city}</div>
              <input 
                type="text" 
                className="profile-field-input" 
                id="cityInput" 
                value={profileData.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
              />
            </div>
            <div className="profile-field">
              <div className="profile-field-label">Work Authorization</div>
              <div className="profile-field-value" id="workAuth">
                {profileData.workAuth === 'citizen' ? 'Citizen - No work permit required' : 
                 profileData.workAuth === 'yes' ? 'Yes - Valid work documents' : 
                 'No - Will need sponsorship'}
              </div>
              <select 
                className="profile-field-input" 
                id="workAuthInput"
                value={profileData.workAuth}
                onChange={(e) => handleInputChange('workAuth', e.target.value)}
              >
                <option value="citizen">Citizen - No work permit required</option>
                <option value="yes">Yes - Valid work documents</option>
                <option value="no">No - Will need sponsorship</option>
              </select>
            </div>
          </div>
          <div className="profile-grid single-column">
            <div className="profile-field">
              <div className="profile-field-label">Full Address</div>
              <div className="profile-field-value" id="address">{profileData.address}</div>
              <input 
                type="text" 
                className="profile-field-input" 
                id="addressInput" 
                value={profileData.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Preferred Locations */}
        <div className="profile-section">
          <h3 className="profile-section-title">
            <FontAwesomeIcon icon={faMapMarkerAlt} />
            Preferred Internship Locations
          </h3>
          <div className="profile-grid">
            <div className="profile-field">
              <div className="profile-field-label">Preferred Location 1</div>
              <div className="profile-field-value" id="prefLoc1">{profileData.prefLoc1}</div>
              <input 
                type="text" 
                className="profile-field-input" 
                id="prefLoc1Input" 
                value={profileData.prefLoc1}
                onChange={(e) => handleInputChange('prefLoc1', e.target.value)}
              />
            </div>
            <div className="profile-field">
              <div className="profile-field-label">Preferred Location 2</div>
              <div className="profile-field-value" id="prefLoc2">{profileData.prefLoc2}</div>
              <input 
                type="text" 
                className="profile-field-input" 
                id="prefLoc2Input" 
                value={profileData.prefLoc2}
                onChange={(e) => handleInputChange('prefLoc2', e.target.value)}
              />
            </div>
            <div className="profile-field">
              <div className="profile-field-label">Preferred Location 3</div>
              <div className="profile-field-value" id="prefLoc3">{profileData.prefLoc3}</div>
              <input 
                type="text" 
                className="profile-field-input" 
                id="prefLoc3Input" 
                value={profileData.prefLoc3}
                onChange={(e) => handleInputChange('prefLoc3', e.target.value)}
              />
            </div>
          </div>
          <div className="profile-grid two-column">
            <div className="profile-field">
              <div className="profile-field-label">Willing to Relocate</div>
              <div className="profile-field-value" id="relocate">
                {profileData.relocate === 'yes' ? 'Yes, anywhere' : 
                 profileData.relocate === 'within-country' ? 'Within my country only' : 
                 'No, local only'}
              </div>
              <select 
                className="profile-field-input" 
                id="relocateInput"
                value={profileData.relocate}
                onChange={(e) => handleInputChange('relocate', e.target.value)}
              >
                <option value="yes">Yes, anywhere</option>
                <option value="within-country">Within my country only</option>
                <option value="no">No, local only</option>
              </select>
            </div>
            <div className="profile-field">
              <div className="profile-field-label">Internship Mode Preference</div>
              <div className="profile-field-value" id="mode">
                {profileData.mode === 'onsite' ? 'On-site' : 
                 profileData.mode === 'remote' ? 'Remote' : 
                 profileData.mode === 'hybrid' ? 'Hybrid' : 
                 'Flexible - Open to all'}
              </div>
              <select 
                className="profile-field-input" 
                id="modeInput"
                value={profileData.mode}
                onChange={(e) => handleInputChange('mode', e.target.value)}
              >
                <option value="onsite">On-site</option>
                <option value="remote">Remote</option>
                <option value="hybrid">Hybrid</option>
                <option value="flexible">Flexible - Open to all</option>
              </select>
            </div>
          </div>
        </div>

        {/* Education */}
        <div className="profile-section">
          <h3 className="profile-section-title">
            <FontAwesomeIcon icon={faGraduationCap} />
            Education
          </h3>
          
          <div className="profile-grid single-column">
            <div className="profile-field">
              <div className="profile-field-label">Current Academic Level</div>
              <div className="profile-field-value" id="academicLevel">
                {profileData.academicLevel === 'high-school' ? 'High School Student' :
                 profileData.academicLevel === 'undergraduate' ? 'Undergraduate/Bachelor\'s Student' :
                 profileData.academicLevel === 'graduate' ? 'Graduate/Master\'s Student' :
                 profileData.academicLevel === 'phd' ? 'PhD Student' :
                 'Recent Graduate'}
              </div>
              <select 
                className="profile-field-input" 
                id="academicLevelInput"
                value={profileData.academicLevel}
                onChange={(e) => handleInputChange('academicLevel', e.target.value)}
              >
                <option value="high-school">High School Student</option>
                <option value="undergraduate">Undergraduate/Bachelor's Student</option>
                <option value="graduate">Graduate/Master's Student</option>
                <option value="phd">PhD Student</option>
                <option value="recent-graduate">Recent Graduate</option>
              </select>
            </div>
            
            <div className="education-item">
              <div className="item-title">University of Nairobi</div>
              <div className="item-subtitle">Bachelor of Science in Computer Science • 2021 - 2025</div>
              <div className="profile-grid two-column" style={{ marginTop: '15px' }}>
                <div className="profile-field">
                  <div className="profile-field-label">Current Year</div>
                  <div className="profile-field-value">3rd Year (Junior)</div>
                </div>
                <div className="profile-field">
                  <div className="profile-field-label">GPA</div>
                  <div className="profile-field-value">3.8/4.0</div>
                </div>
                <div className="profile-field">
                  <div className="profile-field-label">Location</div>
                  <div className="profile-field-value">Nairobi, Kenya</div>
                </div>
                <div className="profile-field">
                  <div className="profile-field-label">Expected Graduation</div>
                  <div className="profile-field-value">May 2025</div>
                </div>
              </div>
              <div style={{ marginTop: '20px' }}>
                <div className="profile-field-label">Relevant Coursework</div>
                <div style={{ marginTop: '8px' }}>
                  <p style={{ color: '#666', fontStyle: 'italic' }}>No coursework added yet</p>
                </div>
              </div>
              <div style={{ marginTop: '20px' }}>
                <div className="profile-field-label">Academic Achievements</div>
                <div className="item-description" style={{ marginTop: '8px' }}>
                  • Dean's List - Fall 2023, Spring 2024<br/>
                  • Academic Excellence Scholarship Recipient<br/>
                  • Best Final Year Project - Computer Science Department<br/>
                  • President of Computer Science Student Association
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Career Objective */}
        <div className="profile-section">
          <h3 className="profile-section-title">
            <FontAwesomeIcon icon={faBullseye} />
            Career Objective & Goals
          </h3>
          <div className="profile-grid single-column">
            <div className="profile-field">
              <div className="profile-field-label">Professional Objective</div>
              <div className="profile-field-value" id="objective">{profileData.objective}</div>
              <textarea 
                className="profile-field-input" 
                id="objectiveInput" 
                rows="4"
                value={profileData.objective}
                onChange={(e) => handleInputChange('objective', e.target.value)}
              />
            </div>
          </div>
          <div className="profile-grid two-column">
            <div className="profile-field">
              <div className="profile-field-label">Industry of Interest</div>
              <div className="profile-field-value" id="industry">
                {profileData.industry === 'technology' ? 'Technology & IT' :
                 profileData.industry === 'finance' ? 'Finance & Banking' :
                 profileData.industry === 'healthcare' ? 'Healthcare & Medical' :
                 profileData.industry === 'marketing' ? 'Marketing & Advertising' :
                 'Consulting'}
              </div>
              <select 
                className="profile-field-input" 
                id="industryInput"
                value={profileData.industry}
                onChange={(e) => handleInputChange('industry', e.target.value)}
              >
                <option value="technology">Technology & IT</option>
                <option value="finance">Finance & Banking</option>
                <option value="healthcare">Healthcare & Medical</option>
                <option value="marketing">Marketing & Advertising</option>
                <option value="consulting">Consulting</option>
              </select>
            </div>
            <div className="profile-field">
              <div className="profile-field-label">Preferred Role</div>
              <div className="profile-field-value" id="role">{profileData.role}</div>
              <input 
                type="text" 
                className="profile-field-input" 
                id="roleInput" 
                value={profileData.role}
                onChange={(e) => handleInputChange('role', e.target.value)}
              />
            </div>
          </div>
          <div className="profile-grid single-column">
            <div className="profile-field">
              <div className="profile-field-label">Career Interests</div>
              <div style={{ marginTop: '8px' }}>
                <p style={{ color: '#666', fontStyle: 'italic' }}>No career interests added yet</p>
              </div>
            </div>
          </div>
        </div>

        {/* Skills */}
        <div className="profile-section">
          <h3 className="profile-section-title">
            <FontAwesomeIcon icon={faLightbulb} />
            Skills & Competencies
          </h3>
          <div className="profile-grid two-column">
            <div className="profile-field">
              <div className="profile-field-label">Programming Languages</div>
              <div style={{ marginTop: '8px' }}>
                <p style={{ color: '#666', fontStyle: 'italic' }}>No programming languages added yet</p>
              </div>
            </div>
            <div className="profile-field">
              <div className="profile-field-label">Web Technologies</div>
              <div style={{ marginTop: '8px' }}>
                <p style={{ color: '#666', fontStyle: 'italic' }}>No web technologies added yet</p>
              </div>
            </div>
            <div className="profile-field">
              <div className="profile-field-label">Tools & Technologies</div>
              <div style={{ marginTop: '8px' }}>
                <p style={{ color: '#666', fontStyle: 'italic' }}>No tools & technologies added yet</p>
              </div>
            </div>
            <div className="profile-field">
              <div className="profile-field-label">Soft Skills</div>
              <div style={{ marginTop: '8px' }}>
                <p style={{ color: '#666', fontStyle: 'italic' }}>No soft skills added yet</p>
              </div>
            </div>
          </div>
        </div>

        {/* Internship Preferences */}
        <div className="profile-section">
          <h3 className="profile-section-title">
            <FontAwesomeIcon icon={faSlidersH} />
            Internship Preferences & Availability
          </h3>
          <div className="profile-grid two-column">
            <div className="profile-field">
              <div className="profile-field-label">Duration Preference</div>
              <div className="profile-field-value" id="duration">
                {profileData.duration === '1-2-months' ? '1-2 months' :
                 profileData.duration === '3-months' ? '3 months' :
                 profileData.duration === '4-6-months' ? '4-6 months' :
                 profileData.duration === '6-12-months' ? '6-12 months' :
                 'Flexible'}
              </div>
              <select 
                className="profile-field-input" 
                id="durationInput"
                value={profileData.duration}
                onChange={(e) => handleInputChange('duration', e.target.value)}
              >
                <option value="1-2-months">1-2 months</option>
                <option value="3-months">3 months</option>
                <option value="4-6-months">4-6 months</option>
                <option value="6-12-months">6-12 months</option>
                <option value="flexible">Flexible</option>
              </select>
            </div>
            <div className="profile-field">
              <div className="profile-field-label">Availability to Start</div>
              <div className="profile-field-value" id="availability">
                {profileData.availability === 'immediate' ? 'Immediately Available' :
                 profileData.availability === '1-week' ? 'Within 1 week' :
                 profileData.availability === '2-weeks' ? 'Within 2 weeks' :
                 profileData.availability === '1-month' ? 'Within 1 month' :
                 profileData.availability === 'summer' ? 'Summer Break' :
                 'Next Semester'}
              </div>
              <select 
                className="profile-field-input" 
                id="availabilityInput"
                value={profileData.availability}
                onChange={(e) => handleInputChange('availability', e.target.value)}
              >
                <option value="immediate">Immediately Available</option>
                <option value="1-week">Within 1 week</option>
                <option value="2-weeks">Within 2 weeks</option>
                <option value="1-month">Within 1 month</option>
                <option value="summer">Summer Break</option>
                <option value="semester">Next Semester</option>
              </select>
            </div>
            <div className="profile-field">
              <div className="profile-field-label">Timing Preference</div>
              <div className="profile-field-value" id="timing">
                {profileData.timing === 'full-time' ? 'Full-time (during break)' :
                 profileData.timing === 'part-time' ? 'Part-time (during semester)' :
                 'Flexible'}
              </div>
              <select 
                className="profile-field-input" 
                id="timingInput"
                value={profileData.timing}
                onChange={(e) => handleInputChange('timing', e.target.value)}
              >
                <option value="full-time">Full-time (during break)</option>
                <option value="part-time">Part-time (during semester)</option>
                <option value="flexible">Flexible</option>
              </select>
            </div>
            <div className="profile-field">
              <div className="profile-field-label">Expected Stipend (Monthly)</div>
              <div className="profile-field-value" id="stipend">{profileData.stipend}</div>
              <input 
                type="text" 
                className="profile-field-input" 
                id="stipendInput" 
                value={profileData.stipend}
                onChange={(e) => handleInputChange('stipend', e.target.value)}
              />
            </div>
            <div className="profile-field">
              <div className="profile-field-label">Willing to Accept Unpaid</div>
              <div className="profile-field-value" id="unpaid">
                {profileData.unpaid === 'yes' ? 'Yes' :
                 profileData.unpaid === 'prefer-paid' ? 'Prefer paid but open' :
                 'No, paid only'}
              </div>
              <select 
                className="profile-field-input" 
                id="unpaidInput"
                value={profileData.unpaid}
                onChange={(e) => handleInputChange('unpaid', e.target.value)}
              >
                <option value="yes">Yes</option>
                <option value="prefer-paid">Prefer paid but open</option>
                <option value="no">No, paid only</option>
              </select>
            </div>
            <div className="profile-field">
              <div className="profile-field-label">Academic Credit Required</div>
              <div className="profile-field-value" id="credit">
                {profileData.credit === 'yes' ? 'Yes, required' :
                 profileData.credit === 'preferred' ? 'Preferred but not required' :
                 'No'}
              </div>
              <select 
                className="profile-field-input" 
                id="creditInput"
                value={profileData.credit}
                onChange={(e) => handleInputChange('credit', e.target.value)}
              >
                <option value="yes">Yes, required</option>
                <option value="preferred">Preferred but not required</option>
                <option value="no">No</option>
              </select>
            </div>
          </div>
        </div>

        {/* Certifications & Training */}
        <div className="profile-section">
          <h3 className="profile-section-title">
            <FontAwesomeIcon icon={faTrophy} />
            Certifications & Training
          </h3>
          
          <div className="profile-grid single-column">
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#666' }}>
              <FontAwesomeIcon icon={faCertificate} style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.3 }} />
              <p style={{ fontSize: '16px', margin: '0' }}>No certifications added yet</p>
              <p style={{ fontSize: '14px', margin: '8px 0 0 0', opacity: 0.7 }}>Add your certifications and training to showcase your professional development</p>
            </div>
          </div>
        </div>

        {/* Extracurricular Activities & Leadership */}
        <div className="profile-section">
          <h3 className="profile-section-title">
            <FontAwesomeIcon icon={faUser} />
            Extracurricular Activities & Leadership
          </h3>
          
          <div className="profile-grid single-column">
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#666' }}>
              <FontAwesomeIcon icon={faUser} style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.3 }} />
              <p style={{ fontSize: '16px', margin: '0' }}>No activities added yet</p>
              <p style={{ fontSize: '14px', margin: '8px 0 0 0', opacity: 0.7 }}>Add your extracurricular activities and leadership roles to showcase your involvement</p>
            </div>
          </div>
        </div>

        {/* Additional Information */}
        <div className="profile-section">
          <h3 className="profile-section-title">
            <FontAwesomeIcon icon={faInfoCircle} />
            Additional Information
          </h3>
          <div className="profile-grid single-column">
            <div className="profile-field">
              <div className="profile-field-label">Hobbies & Interests</div>
              <div className="profile-field-value" id="hobbies">{profileData.hobbies}</div>
              <textarea 
                className="profile-field-input" 
                id="hobbiesInput" 
                rows="3"
                value={profileData.hobbies}
                onChange={(e) => handleInputChange('hobbies', e.target.value)}
              />
            </div>
            <div className="profile-field">
              <div className="profile-field-label">Why Seeking an Internship</div>
              <div className="profile-field-value" id="whyInternship">{profileData.whyInternship}</div>
              <textarea 
                className="profile-field-input" 
                id="whyInternshipInput" 
                rows="4"
                value={profileData.whyInternship}
                onChange={(e) => handleInputChange('whyInternship', e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Edit Actions */}
        {isEditMode && (
          <div className="edit-actions">
            <button className="btn" onClick={saveProfile} style={{ background: '#22c55e', color: 'white' }}>
              <FontAwesomeIcon icon={faSave} /> Save Changes
            </button>
            <button className="btn" onClick={cancelEdit} style={{ background: '#6b7280', color: 'white' }}>
              <FontAwesomeIcon icon={faTimes} /> Cancel
            </button>
          </div>
        )}

        {/* Work Experience & Internships */}
        <div className="profile-section">
          <h3 className="profile-section-title">
            <FontAwesomeIcon icon={faBriefcase} />
            Work Experience & Internships
          </h3>
          
          <div className="profile-grid single-column">
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#666' }}>
              <FontAwesomeIcon icon={faBriefcase} style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.3 }} />
              <p style={{ fontSize: '16px', margin: '0' }}>No work experience added yet</p>
              <p style={{ fontSize: '14px', margin: '8px 0 0 0', opacity: 0.7 }}>Add your internships and work experience to showcase your professional background</p>
            </div>
          </div>
        </div>

        {/* Projects & Portfolio */}
        <div className="profile-section">
          <h3 className="profile-section-title">
            <FontAwesomeIcon icon={faCode} />
            Academic Projects & Portfolio
          </h3>
          
          <div className="profile-grid single-column">
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#666' }}>
              <FontAwesomeIcon icon={faCode} style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.3 }} />
              <p style={{ fontSize: '16px', margin: '0' }}>No projects added yet</p>
              <p style={{ fontSize: '14px', margin: '8px 0 0 0', opacity: 0.7 }}>Add your academic projects and portfolio to showcase your technical skills</p>
            </div>
          </div>
        </div>

        {/* Languages */}
        <div className="profile-section">
          <h3 className="profile-section-title">
            <FontAwesomeIcon icon={faLanguage} />
            Languages
          </h3>
          <div className="profile-grid two-column">
            <div style={{ textAlign: 'center', padding: '20px', color: '#666', gridColumn: '1 / -1' }}>
              <p style={{ fontSize: '14px', margin: '0', fontStyle: 'italic' }}>No languages added yet</p>
            </div>
          </div>
        </div>

        {/* Academic Projects & Portfolio */}
        <div className="profile-section">
          <h3 className="profile-section-title">
            <FontAwesomeIcon icon={faProjectDiagram} />
            Academic Projects & Portfolio
          </h3>
          
          <div style={{ textAlign: 'center', padding: '40px 20px', color: '#666' }}>
            <FontAwesomeIcon icon={faProjectDiagram} style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.3 }} />
            <p style={{ fontSize: '16px', margin: '0' }}>No projects added yet</p>
            <p style={{ fontSize: '14px', margin: '8px 0 0 0', opacity: 0.7 }}>Add your academic projects and portfolio to showcase your technical skills</p>
          </div>
        </div>

        {/* Extracurricular Activities & Leadership */}
        <div className="profile-section">
          <h3 className="profile-section-title">
            <FontAwesomeIcon icon={faUser} />
            Extracurricular Activities & Leadership
          </h3>
          
          <div className="info-box" style={{ marginBottom: '15px' }}>
            <div style={{ fontWeight: '600', marginBottom: '5px' }}>President - Computer Science Student Society</div>
            <div style={{ color: '#666', marginBottom: '8px' }}>2023 - Present</div>
            <div style={{ fontSize: '14px', color: '#555' }}>
              Lead a team of 15 students in organizing tech workshops, hackathons, and networking events. Increased membership by 40% and secured sponsorship for 3 major events.
            </div>
          </div>

          <div className="info-box">
            <div style={{ fontWeight: '600', marginBottom: '5px' }}>Volunteer Tutor - Code for All Initiative</div>
            <div style={{ color: '#666', marginBottom: '8px' }}>2022 - Present</div>
            <div style={{ fontSize: '14px', color: '#555' }}>
              Teach basic programming to underprivileged high school students on weekends. Helped over 50 students learn fundamentals of Python and web development.
            </div>
          </div>
        </div>

        {/* Certifications & Training */}
        <div className="profile-section">
          <h3 className="profile-section-title">
            <FontAwesomeIcon icon={faTrophy} />
            Certifications & Training
          </h3>
          <div className="info-box" style={{ marginBottom: '15px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
              <div>
                <div style={{ fontWeight: '600', marginBottom: '5px' }}>Google Data Analytics Professional Certificate</div>
                <div style={{ color: '#666' }}>Google • Issued March 2024</div>
              </div>
              <a href="#" style={{ color: '#22c55e', whiteSpace: 'nowrap', marginLeft: '15px' }}><FontAwesomeIcon icon={faEye} /> View</a>
            </div>
          </div>
          <div className="info-box">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
              <div>
                <div style={{ fontWeight: '600', marginBottom: '5px' }}>AWS Cloud Practitioner</div>
                <div style={{ color: '#666' }}>Amazon Web Services • Issued January 2024</div>
              </div>
              <a href="#" style={{ color: '#22c55e', whiteSpace: 'nowrap', marginLeft: '15px' }}><FontAwesomeIcon icon={faEye} /> View</a>
            </div>
          </div>
        </div>

        {/* Online Presence & Portfolio */}
        <div className="profile-section">
          <h3 className="profile-section-title">
            <FontAwesomeIcon icon={faCode} />
            Online Presence & Portfolio
          </h3>
          <div className="profile-grid">
            <div className="info-box">
              <div style={{ fontWeight: '600', marginBottom: '5px' }}><FontAwesomeIcon icon={faCode} /> LinkedIn</div>
              <a href="https://linkedin.com/in/emilystudent" target="_blank" style={{ color: '#22c55e', wordBreak: 'break-all' }}>linkedin.com/in/emilystudent</a>
            </div>
            <div className="info-box">
              <div style={{ fontWeight: '600', marginBottom: '5px' }}><FontAwesomeIcon icon={faCode} /> GitHub</div>
              <a href="https://github.com/emilystudent" target="_blank" style={{ color: '#22c55e', wordBreak: 'break-all' }}>github.com/emilystudent</a>
            </div>
            <div className="info-box">
              <div style={{ fontWeight: '600', marginBottom: '5px' }}><FontAwesomeIcon icon={faCode} /> Personal Portfolio</div>
              <a href="https://emilystudent.com" target="_blank" style={{ color: '#22c55e', wordBreak: 'break-all' }}>emilystudent.com</a>
            </div>
          </div>
        </div>

        {/* Edit Actions (Hidden by default) */}
        <div className="edit-actions" style={{ display: isEditMode ? 'flex' : 'none' }}>
          <button className="btn btn-primary" onClick={saveProfile}>
            <FontAwesomeIcon icon={faSave} /> Save Changes
          </button>
          <button className="btn btn-secondary" onClick={cancelEdit}>
            <FontAwesomeIcon icon={faTimes} /> Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// Academic Section
const AcademicSection = ({ academicInfo: initialAcademicInfo, refreshDashboard }) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [academicData, setAcademicData] = useState({
    university: initialAcademicInfo?.university || '',
    major: initialAcademicInfo?.major || '',
    currentYear: initialAcademicInfo?.currentYear || '',
    gpa: initialAcademicInfo?.gpa || '',
    graduation: initialAcademicInfo?.graduation || '',
    fieldOfStudy: initialAcademicInfo?.fieldOfStudy || '',
    degree: initialAcademicInfo?.degree || '',
    startDate: initialAcademicInfo?.startDate || '',
    endDate: initialAcademicInfo?.endDate || ''
  });
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  // Update local state when academicInfo prop changes
  useEffect(() => {
    if (initialAcademicInfo) {
      setAcademicData({
        university: initialAcademicInfo?.university || '',
        major: initialAcademicInfo?.major || '',
        currentYear: initialAcademicInfo?.currentYear || '',
        gpa: initialAcademicInfo?.gpa || '',
        graduation: initialAcademicInfo?.graduation || '',
        fieldOfStudy: initialAcademicInfo?.fieldOfStudy || '',
        degree: initialAcademicInfo?.degree || '',
        startDate: initialAcademicInfo?.startDate || '',
        endDate: initialAcademicInfo?.endDate || ''
      });
    }
  }, [initialAcademicInfo]);

  const handleInputChange = (field, value) => {
    setAcademicData(prev => ({ ...prev, [field]: value }));
  };

  const handleEdit = () => {
    setIsEditMode(true);
    setSaveMessage('');
  };

  const handleCancel = () => {
    setIsEditMode(false);
    // Reset to original data
    setAcademicData({
      university: initialAcademicInfo?.university || '',
      major: initialAcademicInfo?.major || '',
      currentYear: initialAcademicInfo?.currentYear || '',
      gpa: initialAcademicInfo?.gpa || '',
      graduation: initialAcademicInfo?.graduation || '',
      fieldOfStudy: initialAcademicInfo?.fieldOfStudy || '',
      degree: initialAcademicInfo?.degree || '',
      startDate: initialAcademicInfo?.startDate || '',
      endDate: initialAcademicInfo?.endDate || ''
    });
    setSaveMessage('');
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setSaveMessage('');
      
      const token = localStorage.getItem('token');
      if (!token) {
        setSaveMessage('Please login to save changes');
        return;
      }

      const response = await axios.post(
        buildApiUrl('/api/intern/profile'),
        {
          educationEntries: [{
            institution: academicData.university,
            degree: academicData.degree || 'Bachelor of Science',
            fieldOfStudy: academicData.major,
            currentYear: academicData.currentYear,
            gpa: academicData.gpa,
            startDate: academicData.startDate,
            endDate: academicData.graduation
          }]
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        setSaveMessage('Academic information saved successfully! ✓');
        setIsEditMode(false);
        
        // Refresh dashboard data to show updated info
        if (refreshDashboard) {
          refreshDashboard();
        }
        
        // Clear success message after 3 seconds
        setTimeout(() => setSaveMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error saving academic info:', error);
      setSaveMessage('Error saving data. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div>
      <h1 style={{ marginBottom: '25px' }}>Academic Information</h1>
      
      {saveMessage && (
        <div className={`alert ${saveMessage.includes('Error') ? 'warning' : 'success'}`} style={{ marginBottom: '20px' }}>
          {saveMessage}
        </div>
      )}
      
      <div className="card">
        <div className="card-header" style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          padding: '20px'
        }}>
          <h3 className="card-title" style={{ margin: 0 }}>
            <FontAwesomeIcon icon={faGraduationCap} style={{ color: '#f97316', marginRight: '10px' }} /> 
            <span style={{ color: '#0d9488' }}>Education Details</span>
          </h3>
          {!isEditMode ? (
            <button className="btn btn-primary btn-sm" onClick={handleEdit}>
              <FontAwesomeIcon icon={faEdit} /> Edit
            </button>
          ) : (
            <div style={{ display: 'flex', gap: '10px' }}>
              <button 
                className="btn btn-primary btn-sm" 
                onClick={handleSave}
                disabled={isSaving}
              >
                <FontAwesomeIcon icon={faSave} /> {isSaving ? 'Saving...' : 'Save'}
              </button>
              <button 
                className="btn btn-secondary btn-sm" 
                onClick={handleCancel}
                disabled={isSaving}
              >
                <FontAwesomeIcon icon={faTimes} /> Cancel
              </button>
            </div>
          )}
        </div>
        
        <div className="profile-info-grid" style={{ padding: '20px' }}>
          <div>
            <label style={{ fontWeight: '600', color: '#64748b', marginBottom: '8px', display: 'block' }}>
              University
            </label>
            {!isEditMode ? (
              <p style={{ color: '#1e293b', fontSize: '15px', margin: 0 }}>
                {academicData.university || 'Not provided'}
              </p>
            ) : (
              <input
                type="text"
                value={academicData.university}
                onChange={(e) => handleInputChange('university', e.target.value)}
                placeholder="Enter university name"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  transition: 'border-color 0.3s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#f97316'}
                onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
              />
            )}
          </div>
          
          <div>
            <label style={{ fontWeight: '600', color: '#64748b', marginBottom: '8px', display: 'block' }}>
              Major / Field of Study
            </label>
            {!isEditMode ? (
              <p style={{ color: '#1e293b', fontSize: '15px', margin: 0 }}>
                {academicData.major || 'Not provided'}
              </p>
            ) : (
              <input
                type="text"
                value={academicData.major}
                onChange={(e) => handleInputChange('major', e.target.value)}
                placeholder="Enter your major"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none'
                }}
                onFocus={(e) => e.target.style.borderColor = '#f97316'}
                onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
              />
            )}
          </div>
          
          <div>
            <label style={{ fontWeight: '600', color: '#64748b', marginBottom: '8px', display: 'block' }}>
              Current Year
            </label>
            {!isEditMode ? (
              <p style={{ color: '#1e293b', fontSize: '15px', margin: 0 }}>
                {academicData.currentYear || 'Not provided'}
              </p>
            ) : (
              <select
                value={academicData.currentYear}
                onChange={(e) => handleInputChange('currentYear', e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none'
                }}
                onFocus={(e) => e.target.style.borderColor = '#f97316'}
                onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
              >
                <option value="">Select Year</option>
                <option value="1st Year (Freshman)">1st Year (Freshman)</option>
                <option value="2nd Year (Sophomore)">2nd Year (Sophomore)</option>
                <option value="3rd Year (Junior)">3rd Year (Junior)</option>
                <option value="4th Year (Senior)">4th Year (Senior)</option>
                <option value="Graduate Student">Graduate Student</option>
                <option value="Recent Graduate">Recent Graduate</option>
              </select>
            )}
          </div>
          
          <div>
            <label style={{ fontWeight: '600', color: '#64748b', marginBottom: '8px', display: 'block' }}>
              GPA
            </label>
            {!isEditMode ? (
              <p style={{ color: '#1e293b', fontSize: '15px', margin: 0 }}>
                {academicData.gpa || 'Not provided'}
              </p>
            ) : (
              <input
                type="text"
                value={academicData.gpa}
                onChange={(e) => handleInputChange('gpa', e.target.value)}
                placeholder="e.g., 3.8/4.0"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none'
                }}
                onFocus={(e) => e.target.style.borderColor = '#f97316'}
                onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
              />
            )}
          </div>
          
          <div>
            <label style={{ fontWeight: '600', color: '#64748b', marginBottom: '8px', display: 'block' }}>
              Expected Graduation
            </label>
            {!isEditMode ? (
              <p style={{ color: '#1e293b', fontSize: '15px', margin: 0 }}>
                {academicData.graduation || 'Not provided'}
              </p>
            ) : (
              <input
                type="month"
                value={academicData.graduation ? academicData.graduation.substring(0, 7) : ''}
                onChange={(e) => handleInputChange('graduation', e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none'
                }}
                onFocus={(e) => e.target.style.borderColor = '#f97316'}
                onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
              />
            )}
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop: '20px' }}>
        <div className="card-header">
          <h3 className="card-title"><FontAwesomeIcon icon={faBook} /> Relevant Coursework</h3>
          <button className="btn btn-primary btn-sm">
            <FontAwesomeIcon icon={faPlus} /> Add Course
          </button>
        </div>
        <div>
          <p style={{ color: '#666', fontStyle: 'italic', textAlign: 'center', margin: '20px 0' }}>
            No coursework added yet
          </p>
        </div>
      </div>
    </div>
  );
};

// Learning Resources Section
const LearningResourcesSection = ({ user }) => {
  const [recommendations, setRecommendations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    fetchRecommendations();
  }, []);

  const fetchRecommendations = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        console.warn('No token found, skipping learning recommendations fetch');
        setLoading(false);
        return;
      }
      
      const response = await axios.get(
        buildApiUrl('/api/intern/learning-recommendations'),
        { 
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000 // 10 second timeout
        }
      );

      if (response.data && response.data.success) {
        setRecommendations(response.data.recommendations);
      } else {
        console.warn('Learning recommendations response missing success flag:', response.data);
      }
    } catch (error) {
      console.error('Error fetching learning recommendations:', error);
      
      // If it's a network error, show a user-friendly message
      if (error.code === 'ECONNABORTED' || error.message.includes('Network Error') || error.message.includes('ERR_CONNECTION_REFUSED')) {
        console.warn('⚠️ Backend server may not be running or accessible. Using default recommendations.');
        // Set empty recommendations so the UI doesn't break
        setRecommendations({
          courses: [],
          challenges: [],
          career: [],
          certifications: []
        });
      } else if (error.response) {
        // Server responded with error status
        console.error('Server error:', error.response.status, error.response.data);
      }
    } finally {
      setLoading(false);
    }
  };

  const getIconForCategory = (category) => {
    const iconMap = {
      'book': faBook,
      'code': faCode,
      'chart-line': faChartLine,
      'trophy': faTrophy
    };
    return iconMap[category] || faBook;
  };

  const getCategoryTitle = (category) => {
    const titles = {
      'courses': 'Online Courses',
      'challenges': 'Coding Challenges',
      'career': 'Career Resources',
      'certifications': 'Certifications'
    };
    return titles[category] || category;
  };

  const getCategoryColor = (color) => {
    return color || '#10b981';
  };

  const getAllResources = () => {
    if (!recommendations) return [];
    return [
      ...recommendations.courses || [],
      ...recommendations.challenges || [],
      ...recommendations.career || [],
      ...recommendations.certifications || []
    ].sort((a, b) => b.matchScore - a.matchScore);
  };

  const getFilteredResources = () => {
    if (!recommendations) return [];
    if (activeTab === 'all') return getAllResources();
    return recommendations[activeTab] || [];
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '48px', color: '#f59e0b', marginBottom: '20px' }} />
        <p style={{ fontSize: '18px', color: '#666' }}>🤖 AI is analyzing your profile to find the best learning resources...</p>
      </div>
    );
  }

  // If no recommendations and not loading, show a helpful message
  if (!recommendations || (!recommendations.courses && !recommendations.challenges && !recommendations.career && !recommendations.certifications)) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <FontAwesomeIcon icon={faBook} style={{ fontSize: '48px', color: '#d1d5db', marginBottom: '20px' }} />
        <h3 style={{ color: '#666', marginBottom: '10px' }}>Learning Resources</h3>
        <p style={{ fontSize: '16px', color: '#999' }}>
          Unable to load recommendations at this time. Please try refreshing the page.
        </p>
        <button
          onClick={fetchRecommendations}
          style={{
            marginTop: '20px',
            padding: '10px 20px',
            background: 'linear-gradient(135deg, #f59e0b 0%, #0d9488 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Category Tabs */}
      <div style={{ 
        display: 'flex', 
        gap: '10px', 
        marginBottom: '30px', 
        borderBottom: '2px solid #e5e7eb',
        flexWrap: 'wrap'
      }}>
        <button
          onClick={() => setActiveTab('all')}
          style={{
            padding: '12px 24px',
            background: activeTab === 'all' ? 'linear-gradient(135deg, #f59e0b 0%, #0d9488 100%)' : 'transparent',
            color: activeTab === 'all' ? 'white' : '#666',
            border: 'none',
            borderRadius: '8px 8px 0 0',
            cursor: 'pointer',
            fontWeight: activeTab === 'all' ? '600' : '400',
            fontSize: '15px',
            transition: 'all 0.3s ease'
          }}
        >
          All Recommendations ({getAllResources().length})
        </button>
        <button
          onClick={() => setActiveTab('courses')}
          style={{
            padding: '12px 24px',
            background: activeTab === 'courses' ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' : 'transparent',
            color: activeTab === 'courses' ? 'white' : '#666',
            border: 'none',
            borderRadius: '8px 8px 0 0',
            cursor: 'pointer',
            fontWeight: activeTab === 'courses' ? '600' : '400',
            fontSize: '15px',
            transition: 'all 0.3s ease'
          }}
        >
          <FontAwesomeIcon icon={faBook} /> Courses ({recommendations?.courses?.length || 0})
        </button>
        <button
          onClick={() => setActiveTab('challenges')}
          style={{
            padding: '12px 24px',
            background: activeTab === 'challenges' ? 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)' : 'transparent',
            color: activeTab === 'challenges' ? 'white' : '#666',
            border: 'none',
            borderRadius: '8px 8px 0 0',
            cursor: 'pointer',
            fontWeight: activeTab === 'challenges' ? '600' : '400',
            fontSize: '15px',
            transition: 'all 0.3s ease'
          }}
        >
          <FontAwesomeIcon icon={faCode} /> Challenges ({recommendations?.challenges?.length || 0})
        </button>
        <button
          onClick={() => setActiveTab('career')}
          style={{
            padding: '12px 24px',
            background: activeTab === 'career' ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' : 'transparent',
            color: activeTab === 'career' ? 'white' : '#666',
            border: 'none',
            borderRadius: '8px 8px 0 0',
            cursor: 'pointer',
            fontWeight: activeTab === 'career' ? '600' : '400',
            fontSize: '15px',
            transition: 'all 0.3s ease'
          }}
        >
          <FontAwesomeIcon icon={faChartLine} /> Career ({recommendations?.career?.length || 0})
        </button>
        <button
          onClick={() => setActiveTab('certifications')}
          style={{
            padding: '12px 24px',
            background: activeTab === 'certifications' ? 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)' : 'transparent',
            color: activeTab === 'certifications' ? 'white' : '#666',
            border: 'none',
            borderRadius: '8px 8px 0 0',
            cursor: 'pointer',
            fontWeight: activeTab === 'certifications' ? '600' : '400',
            fontSize: '15px',
            transition: 'all 0.3s ease'
          }}
        >
          <FontAwesomeIcon icon={faTrophy} /> Certifications ({recommendations?.certifications?.length || 0})
        </button>
      </div>

      {/* Resource Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '20px' }}>
        {getFilteredResources().map((resource, index) => (
          <div 
            key={resource.id}
            className="card"
            style={{
              borderTop: `4px solid ${getCategoryColor(resource.color)}`,
              transition: 'all 0.3s ease',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-5px)';
              e.currentTarget.style.boxShadow = '0 10px 25px rgba(0,0,0,0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '';
            }}
          >
            {/* Match Score Badge */}
            <div style={{ 
              position: 'absolute', 
              top: '15px', 
              right: '15px',
              background: 'linear-gradient(135deg, #f59e0b 0%, #0d9488 100%)',
              color: 'white',
              padding: '6px 12px',
              borderRadius: '20px',
              fontSize: '13px',
              fontWeight: '600',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
            }}>
              {resource.matchScore}% Match
            </div>

            {/* Icon */}
            <div style={{
              width: '60px',
              height: '60px',
              borderRadius: '15px',
              background: getCategoryColor(resource.color),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '20px',
              fontSize: '28px',
              color: 'white'
            }}>
              <FontAwesomeIcon icon={getIconForCategory(resource.icon)} />
            </div>

            {/* Title & Platform */}
            <h3 style={{ marginBottom: '8px', fontSize: '18px', color: '#1f2937' }}>{resource.title}</h3>
            <p style={{ 
              fontSize: '13px', 
              color: '#f59e0b', 
              fontWeight: '600',
              marginBottom: '12px'
            }}>
              {resource.platform} • {resource.difficulty} • {resource.duration}
            </p>

            {/* Description */}
            <p style={{ color: '#666', marginBottom: '15px', lineHeight: '1.5' }}>
              {resource.description}
            </p>

            {/* Why Recommended */}
            <div style={{
              background: '#f0fdf4',
              border: '1px solid #86efac',
              borderRadius: '8px',
              padding: '12px',
              marginBottom: '15px'
            }}>
              <p style={{ 
                fontSize: '13px', 
                color: '#166534', 
                margin: 0,
                fontWeight: '500'
              }}>
                <FontAwesomeIcon icon={faStar} style={{ color: '#f59e0b', marginRight: '6px' }} />
                {resource.recommendationReason}
              </p>
            </div>

            {/* Skills Tags */}
            <div style={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: '6px', 
              marginBottom: '15px' 
            }}>
              {resource.skills.slice(0, 4).map((skill, idx) => (
                <span 
                  key={idx}
                  style={{
                    background: '#f3f4f6',
                    color: '#374151',
                    padding: '4px 10px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '500'
                  }}
                >
                  {skill}
                </span>
              ))}
              {resource.skills.length > 4 && (
                <span style={{
                  color: '#9ca3af',
                  padding: '4px 10px',
                  fontSize: '12px'
                }}>
                  +{resource.skills.length - 4} more
                </span>
              )}
            </div>

            {/* Action Button */}
            <button 
              className="btn btn-primary btn-sm"
              style={{
                width: '100%',
                background: 'linear-gradient(135deg, #f59e0b 0%, #0d9488 100%)',
                border: 'none',
                padding: '12px',
                fontSize: '14px',
                fontWeight: '600'
              }}
              onClick={() => window.open(resource.url, '_blank')}
            >
              {resource.category === 'Online Course' ? 'Explore Course' :
               resource.category === 'Coding Challenge' ? 'Start Practicing' :
               resource.category === 'Career Resource' ? 'Read More' :
               'View Certification'}
            </button>

            {/* Rating */}
            {resource.rating && (
              <div style={{ 
                textAlign: 'center', 
                marginTop: '10px',
                fontSize: '13px',
                color: '#666'
              }}>
                <FontAwesomeIcon icon={faStar} style={{ color: '#f59e0b' }} /> {resource.rating}/5.0
              </div>
            )}
          </div>
        ))}
      </div>

      {getFilteredResources().length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: '60px 20px' }}>
          <FontAwesomeIcon icon={faBook} style={{ fontSize: '64px', color: '#ccc', marginBottom: '20px' }} />
          <h3 style={{ color: '#666' }}>No resources found</h3>
          <p style={{ color: '#999' }}>Try selecting a different category or complete your profile for better recommendations</p>
        </div>
      )}
    </div>
  );
};

export default InternDashboardComplete;

